/**
 * Cliente HTTP para o backend Auction (SaaS).
 * Base URL: VITE_API_URL (build) se definido; senão derivada do host; senão mesma origem ('').
 * auction.elicitacao.cloud: por omissão usa https://api.elicitacao.cloud (deploy Nixpacks + API separada).
 * Para mesma origem (Dockerfile + Nginx + BACKEND_UPSTREAM): build com VITE_USE_SAME_ORIGIN_API=true ou VITE_USE_SUBDOMAIN_API=0.
 */
function envFlagTrue(v) {
  return v === '1' || v === 'true' || v === true;
}
function envFlagFalse(v) {
  return v === '0' || v === 'false' || v === false;
}

/** Fallback automático para mesma origem (/api no Nginx) — só quando o build pede explicitamente. */
function sameOriginApiFallbackEnabled() {
  return envFlagTrue(import.meta.env.VITE_USE_SAME_ORIGIN_API);
}

/** API pública de produção — não usar no PC com credenciais seed (@auction.local). */
function isProductionAuctionApiBaseUrl(url) {
  if (!url || typeof url !== 'string') return false;
  const s = url.trim().replace(/\/$/, '');
  if (!s) return false;
  try {
    const u = new URL(s.startsWith('http') ? s : `https://${s}`);
    const host = u.hostname.toLowerCase();
    return host === 'api.elicitacao.cloud' || host === 'api.auction.com';
  } catch {
    return false;
  }
}

/** Em `vite` no localhost, VITE_API_URL copiado do deploy aponta para a nuvem por engano. */
let forcedLocalApiOverProdEnv = false;

function resolveApiUrl() {
  const envUrl = import.meta.env.VITE_API_URL;
  const trimmedEnv =
    envUrl && typeof envUrl === 'string' && envUrl.trim() ? envUrl.trim().replace(/\/$/, '') : '';

  if (typeof window !== 'undefined' && window.location?.hostname) {
    const h = window.location.hostname;
    const isLocalBrowser = h === 'localhost' || h === '127.0.0.1';
    if (import.meta.env.DEV && isLocalBrowser && trimmedEnv && isProductionAuctionApiBaseUrl(trimmedEnv)) {
      if (!envFlagTrue(import.meta.env.VITE_USE_PROD_API)) {
        forcedLocalApiOverProdEnv = true;
        return '';
      }
    }
  }

  if (trimmedEnv) return trimmedEnv;

  if (typeof window !== 'undefined' && window.location?.hostname) {
    const h = window.location.hostname;
    if (h === 'auction.elicitacao.cloud') {
      if (envFlagTrue(import.meta.env.VITE_USE_SAME_ORIGIN_API)) return '';
      if (envFlagFalse(import.meta.env.VITE_USE_SUBDOMAIN_API)) return '';
      return 'https://api.elicitacao.cloud';
    }
    if (h === 'api.elicitacao.cloud') return '';
    if (h === 'auction.com' || h.endsWith('.auction.com')) return 'https://api.auction.com';
    if ((h === 'localhost' || h === '127.0.0.1') && import.meta.env.DEV) return '';
    if (h === 'localhost' || h === '127.0.0.1') return 'http://localhost:3001';
    return '';
  }
  return '';
}
const INITIAL_API_URL = resolveApiUrl();
/** URL efetiva da API: pode mudar em fallbacks (ex.: mesma origem quando api.elicitacao.cloud falha). */
let _effectiveApiBase = INITIAL_API_URL;
const TOKEN_KEY = 'auction_token';

/** Logs de tentativas de download de anexo (defina `VITE_DEBUG_ANEXO=true` no `.env`). */
function isAnexoDownloadDebugEnabled() {
  try {
    return typeof import.meta !== 'undefined' && import.meta.env?.VITE_DEBUG_ANEXO === 'true';
  } catch {
    return false;
  }
}

function getBaseUrl() {
  return _effectiveApiBase;
}

/** Quando debug está off, erros de conexão não são logados no console (páginas com try/catch exibem lista vazia sem ruído). */
let _connectionErrorWarned = false;
/** Evita rodar o diagnóstico automático mais de uma vez por carregamento. */
let _connectionDiagnosticRun = false;
/** Evita tentar fallback para mesma origem mais de uma vez. */
let _sameOriginFallbackTried = false;
/** Evita tentar fallback para API dedicada (subdomínio) mais de uma vez por carregamento. */
let _directApiFallbackTried = false;

/** Codes estáveis no JSON de erro do Nginx (nginx.conf.template, nginx-fullstack.conf, nginx-no-backend.conf). */
export const AUCTION_GATEWAY_CODE_UPSTREAM = 'AUCTION_UPSTREAM_UNAVAILABLE';
export const AUCTION_GATEWAY_CODE_NOT_CONFIGURED = 'AUCTION_BACKEND_NOT_CONFIGURED';

/**
 * Resposta JSON do Nginx do front (proxy/fullstack) ou sem BACKEND_UPSTREAM — não exibir error/hint na UI.
 * Compatível com deploys antigos (só campo `error`) e novos (`code`).
 */
function isGatewayInfraPayload(data) {
  if (!data || typeof data !== 'object') return false;
  const code = data.code;
  if (code === AUCTION_GATEWAY_CODE_UPSTREAM || code === AUCTION_GATEWAY_CODE_NOT_CONFIGURED) return true;
  const err = typeof data.error === 'string' ? data.error : '';
  return err === 'Backend inacessível' || err === 'Backend não configurado';
}

/** @deprecated manter nome para leitura do código; equivale a isGatewayInfraPayload */
function isNginxProxyBackendMissing(data) {
  return isGatewayInfraPayload(data);
}

function isGatewayInfraErrorText(msg) {
  return isGatewayInfraPayload({ error: msg });
}

function resolveDirectApiFallbackUrl() {
  const fromEnv = import.meta.env.VITE_DIRECT_API_FALLBACK;
  if (fromEnv && typeof fromEnv === 'string' && fromEnv.trim()) return fromEnv.trim().replace(/\/$/, '');
  return 'https://api.elicitacao.cloud';
}

/** Timeout curto para sondar se https://api.elicitacao.cloud responde ao carregar a página. */
const DEDICATED_API_PROBE_MS = 6000;

/**
 * Se GET /health na origem (proxy) devolver JSON com status ok, passa a usar mesma origem para /api.
 * @returns {Promise<boolean>}
 */
async function trySwitchToSameOriginFromDedicatedApi() {
  if (!sameOriginApiFallbackEnabled()) return false;
  if (typeof window === 'undefined') return false;
  const origin = window.location.origin;
  if (origin !== 'https://auction.elicitacao.cloud' || _effectiveApiBase !== 'https://api.elicitacao.cloud') return false;
  try {
    const res = await fetch(`${origin}/health`, { method: 'GET', credentials: 'include', mode: 'cors', headers: { Accept: 'application/json' } });
    const text = await res.text();
    let data;
    try {
      data = JSON.parse(text);
    } catch {
      return false;
    }
    if (res.ok && data && data.status === 'ok' && data.service === 'auction-api') {
      _effectiveApiBase = '';
      if (typeof console !== 'undefined') {
        console.warn('[Auction API] api.elicitacao.cloud inacessível; usando mesma origem (proxy).');
      }
      return true;
    }
  } catch {
    // ignore
  }
  return false;
}

/**
 * Se estamos em auction.elicitacao.cloud e a API configurada é api.elicitacao.cloud mas inacessível,
 * tenta /health na mesma origem. Se o backend responder (proxy), usa mesma origem daí em diante.
 */
async function trySameOriginFallback() {
  if (!sameOriginApiFallbackEnabled()) return;
  if (_sameOriginFallbackTried || typeof window === 'undefined') return;
  const origin = window.location.origin;
  if (origin !== 'https://auction.elicitacao.cloud' || _effectiveApiBase !== 'https://api.elicitacao.cloud') return;
  _sameOriginFallbackTried = true;
  await trySwitchToSameOriginFromDedicatedApi();
}

/**
 * Ao abrir o site, verifica rapidamente se a API dedicada responde; se não, tenta o proxy na mesma origem
 * (reduz tempo até o fallback quando há timeout de rede na API pública).
 */
function scheduleDedicatedApiHealthProbe() {
  if (!sameOriginApiFallbackEnabled()) return;
  if (typeof window === 'undefined') return;
  if (window.location.hostname !== 'auction.elicitacao.cloud') return;
  if (INITIAL_API_URL !== 'https://api.elicitacao.cloud' || _effectiveApiBase !== 'https://api.elicitacao.cloud') return;

  queueMicrotask(() => {
    void (async () => {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), DEDICATED_API_PROBE_MS);
      let apiReachable = false;
      try {
        const res = await fetch('https://api.elicitacao.cloud/health', {
          method: 'GET',
          mode: 'cors',
          credentials: 'include',
          headers: { Accept: 'application/json' },
          signal: controller.signal,
        });
        clearTimeout(timer);
        apiReachable = res.ok;
      } catch {
        clearTimeout(timer);
      }
      if (apiReachable) return;
      if (_sameOriginFallbackTried) return;
      const switched = await trySwitchToSameOriginFromDedicatedApi();
      if (switched) _sameOriginFallbackTried = true;
    })();
  });
}

// Depuração: em dev ou com flag, loga a URL da API ao carregar (ajuda a ver se está correta)
if (typeof window !== 'undefined') {
  const dev = import.meta.env.DEV;
  const debugFlag = typeof localStorage !== 'undefined' && (localStorage.getItem('auction_debug_api') === '1' || sessionStorage.getItem('auction_debug_api') === '1');
  if (dev || debugFlag) {
    const base = getBaseUrl() || window.location.origin;
    const healthUrl = getBaseUrl() ? `${getBaseUrl()}/health` : `${window.location.origin}/health`;
    console.log('[Auction API] Base URL:', base || '(mesma origem)');
    console.log('[Auction API] Health check:', healthUrl);
    if (forcedLocalApiOverProdEnv && dev) {
      console.warn(
        '[Auction API] Seu .env define VITE_API_URL para a API de produção. Em dev no localhost usamos o proxy do Vite → http://localhost:3001 (credenciais seed @auction.local só existem no seu Postgres local). Remova VITE_API_URL do .env ou defina VITE_USE_PROD_API=true se quiser testar contra a nuvem.'
      );
    }
    if (debugFlag) console.log('[Auction API] Para novo diagnóstico: __auctionApiDebug()');
  }
  scheduleDedicatedApiHealthProbe();
  if (!dev && window.location.hostname === 'auction.elicitacao.cloud') {
    const base = getBaseUrl() || '(mesma origem — defina VITE_API_URL no build)';
    console.info('[Auction] API base:', base, '| upload edital: processamento-jobs (servidor)');
  }
}

/** Mensagem quando não há conexão (rede, DNS, servidor offline). */
export const CONNECTION_ERROR_USER_MESSAGE = 'Não foi possível conectar ao servidor. Verifique sua conexão com a internet e tente novamente. Se o problema persistir, o serviço pode estar temporariamente indisponível.';
/** Mensagem quando o servidor responde 502/503 (indisponível ou em manutenção). */
export const SERVICE_UNAVAILABLE_MESSAGE = 'Serviço temporariamente indisponível. Tente novamente em alguns instantes.';
const SUPER_ADMIN_TOKEN_KEY = 'auction_super_admin_token';

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

export function getSuperAdminToken() {
  return localStorage.getItem(SUPER_ADMIN_TOKEN_KEY);
}

export function setSuperAdminToken(token) {
  if (token) localStorage.setItem(SUPER_ADMIN_TOKEN_KEY, token);
  else localStorage.removeItem(SUPER_ADMIN_TOKEN_KEY);
}

export function getApiUrl() {
  return getBaseUrl();
}

/** Ativar debug de conexão: no console execute localStorage.setItem('auction_debug_api','1') e recarregue a página. */

const HEALTH_CHECK_TIMEOUT_MS = 10000;

/**
 * Diagnóstico de conexão com a API (health check com timeout). Chamar no console: window.__auctionApiDebug()
 * Ou: import { checkApiConnection } from '@/api/apiClient'; checkApiConnection();
 */
/**
 * @param {{ suppressConsoleLog?: boolean }} [options] Se `suppressConsoleLog`, não escreve Diagnóstico/Dica no console (o chamador pode logar uma vez).
 */
export async function checkApiConnection(options = {}) {
  const { suppressConsoleLog = false } = options;
  const base = getBaseUrl();
  const healthPath = base ? `${base}/health` : '/health';
  const fullUrl = typeof window !== 'undefined' && !base ? `${window.location.origin}/health` : healthPath;
  const result = { url: fullUrl, ok: false, status: null, data: null, error: null, hint: '' };
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), HEALTH_CHECK_TIMEOUT_MS);
  try {
    const res = await fetch(healthPath, {
      method: 'GET',
      mode: 'cors',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      signal: controller.signal,
    });
    clearTimeout(timer);
    result.status = res.status;
    result.ok = res.ok;
    const text = await res.text();
    try {
      result.data = JSON.parse(text);
    } catch {
      result.data = text?.slice(0, 200) || null;
    }
    if (!res.ok) {
      result.error = `HTTP ${res.status}`;
      if (res.status === 503 || res.status === 502) {
        result.hint = 'Serviço indisponível (503/502). Verifique: backend no ar, BACKEND_UPSTREAM com o nome real do serviço (ex.: auction-api:3001), proxy e health check no Coolify. Veja COOLIFY-SETUP.md e docs/PRODUCTION-DIAGNOSTIC-503.md.';
      } else if (res.status === 404) {
        result.hint = 'Rota /health não existe no servidor.';
      } else {
        result.hint = 'Servidor respondeu com erro.';
      }
    } else {
      result.hint = 'Servidor acessível. Se o app ainda falha, pode ser CORS em outras rotas ou token.';
    }
  } catch (e) {
    clearTimeout(timer);
    result.error = e?.message || String(e);
    if (e?.name === 'AbortError') {
      result.hint = 'Timeout ao conectar em ' + (HEALTH_CHECK_TIMEOUT_MS / 1000) + 's. Servidor lento ou inacessível.';
    } else {
      result.hint = e?.message?.includes('Failed to fetch') || e?.message?.includes('Load failed')
        ? 'Não foi possível conectar. Possíveis causas: (1) Servidor fora do ar. (2) Certificado SSL inválido. (3) CORS bloqueando. Teste em outra aba: ' + fullUrl
        : (e?.cause?.code || result.error);
    }
  }
  const logDiag =
    import.meta.env.DEV ||
    (typeof localStorage !== 'undefined' &&
      (localStorage.getItem('auction_debug_api') === '1' || sessionStorage.getItem('auction_debug_api') === '1'));
  if (logDiag && !suppressConsoleLog && typeof console !== 'undefined') {
    console.log('[Auction API] Diagnóstico:', result);
    if (result.hint) console.log('[Auction API] Dica:', result.hint);
  }
  return result;
}

if (typeof window !== 'undefined') {
  window.__auctionApiDebug = checkApiConnection;
}

/** Path relativo ou pathname de URL absoluta (política de token / 401). */
function requestPathOnly(pathOrUrl) {
  if (!pathOrUrl || typeof pathOrUrl !== 'string') return '';
  try {
    if (pathOrUrl.startsWith('http')) return new URL(pathOrUrl).pathname || '';
    const q = pathOrUrl.indexOf('?');
    return q === -1 ? pathOrUrl : pathOrUrl.slice(0, q);
  } catch {
    return '';
  }
}

/** Não enviar Bearer: login/cadastro públicos (proxy ou sessão antiga não devem interferir). */
function shouldOmitBearerToken(pathOrUrl) {
  const p = requestPathOnly(pathOrUrl);
  return (
    p === '/auth/login' ||
    p === '/auth/super-admin/login' ||
    p === '/auth/esqueci-senha' ||
    p === '/auth/redefinir-senha' ||
    p === '/empresas/registro'
  );
}

/**
 * Sinaliza sessão expirada para a aplicação (uma vez por janela).
 * O AuthContext escuta `auction:session-expired` e redireciona para o login,
 * evitando que páginas fiquem repetindo 401 (ex.: Histórico após token expirar).
 */
let _sessionExpiredNotified = false;
function notifySessionExpired(detail) {
  if (_sessionExpiredNotified) return;
  _sessionExpiredNotified = true;
  if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
    window.dispatchEvent(new CustomEvent('auction:session-expired', { detail }));
  }
}

/** Reset do flag de sessão expirada (chamar após login bem-sucedido). */
export function resetSessionExpiredFlag() {
  _sessionExpiredNotified = false;
}

/** 401 esperado nesses POST — não remover JWT (ex.: senha errada com sessão ainda válida). */
function shouldSkipTokenClearOn401(method, pathOrUrl) {
  const p = requestPathOnly(pathOrUrl);
  /** AuctionCertify: 401 aqui não invalida JWT do utilizador (senha/errada ou sessão de verificação). */
  if (p.includes('/api/certify/public/verify/')) return true;
  if (method !== 'POST') return false;
  return (
    p === '/auth/login' ||
    p === '/auth/super-admin/login' ||
    p === '/auth/esqueci-senha' ||
    p === '/auth/redefinir-senha' ||
    p === '/empresas/registro'
  );
}

async function request(method, path, body = undefined, options = {}) {
  const url = path.startsWith('http') ? path : `${getBaseUrl()}${path}`;
  const token = getToken();
  const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;
  const { headers: optionsHeadersMerge, ...optionsRest } = options;
  const headers = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...(token && !shouldOmitBearerToken(path) && { Authorization: `Bearer ${token}` }),
    ...(optionsHeadersMerge && typeof optionsHeadersMerge === 'object' ? optionsHeadersMerge : {}),
  };
  
  // Criar AbortController para timeout:
  // - 10 min em chamadas LLM/extract-data/invoke-llm-stream (alinhado a INVOKE_LLM_TIMEOUT_MS)
  // - 90s ao criar job de processamento (deve responder 202 em segundos)
  // - 3 minutos em demais chamadas
  let abortController = null;
  const isProcessamentoJobCreate = method === 'POST' && path.includes('processamento-jobs');
  const isLlmCall =
    path.includes('invoke-llm') ||
    path.includes('extract-data') ||
    isProcessamentoJobCreate;
  const timeoutMs = isProcessamentoJobCreate ? 60_000 : isLlmCall ? 600_000 : 180_000;
  if (!options.signal) {
    abortController = new AbortController();
    setTimeout(() => abortController?.abort(), timeoutMs);
  }
  
  const config = {
    method,
    headers,
    credentials: 'include', // Necessário para CORS com Authorization (server envia Allow-Credentials: true)
    mode: 'cors',
    ...(body != null && { body: isFormData ? body : JSON.stringify(body) }),
    ...optionsRest,
    signal: options.signal || (abortController ? abortController.signal : undefined),
  };
  
  try {
    // Usar fetch com tratamento silencioso para erros 403
    const fetchPromise = fetch(url, config);
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => {
        // Garante que a requisição pare também no nível do fetch (quando possível)
        abortController?.abort?.();
        reject(Object.assign(new Error('A requisição demorou muito para responder. Tente novamente.'), { status: 408, name: 'AbortError' }));
      }, timeoutMs);
    });

    const res = await Promise.race([fetchPromise, timeoutPromise]);
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      // Tratamento específico para erro 403 (Forbidden)
      if (res.status === 403) {
        // Criar erro customizado sem propagar para o console do navegador
        const forbiddenError = Object.assign(
          new Error(data.message ?? data.error ?? 'Acesso negado'),
          {
            status: 403,
            data: data,
            isForbidden: true,
            silent: true,
            name: 'ForbiddenError',
            message: data.message ?? data.error ?? 'Acesso negado',
            toString: () => '', // Suprimir toString para reduzir logs
          }
        );
        // Remover stack trace
        Object.defineProperty(forbiddenError, 'stack', {
          value: '',
          writable: false,
          configurable: true
        });
        throw forbiddenError;
      }
      // Tratamento para erro 401 (Unauthorized)
      if (res.status === 401) {
        const unauthorizedError = new Error(data.message ?? data.error ?? 'Não autenticado. Faça login novamente.');
        unauthorizedError.status = 401;
        unauthorizedError.data = data;
        unauthorizedError.isUnauthorized = true;
        if (!shouldSkipTokenClearOn401(method, path)) {
          const hadToken = !!getToken();
          setToken(null);
          // Sessão genuinamente expirada/inválida: avisa a app para redirecionar ao login.
          if (hadToken || data?.code === 'TOKEN_EXPIRED' || data?.code === 'TOKEN_INVALID') {
            notifySessionExpired({ code: data?.code, message: unauthorizedError.message });
          }
        }
        throw unauthorizedError;
      }
      // 429: rate limit — uma nova tentativa GET após Retry-After (segundos) ou backoff fixo
      if (res.status === 429) {
        const ra = res.headers.get('Retry-After');
        const sec = parseInt(String(ra || '').trim(), 10);
        const retryMs = Number.isFinite(sec) && sec > 0
          ? Math.min(120_000, Math.max(1500, sec * 1000))
          : 8000;
        if (method === 'GET' && !options._retried429) {
          options._retried429 = true;
          await new Promise((r) => setTimeout(r, retryMs));
          return request(method, path, body, options);
        }
        const tooMany = new Error(data.error ?? data.message ?? 'Muitas requisições. Aguarde e tente novamente.');
        tooMany.status = 429;
        tooMany.data = data;
        tooMany.name = 'TooManyRequestsError';
        throw tooMany;
      }
      // 502/503: serviço indisponível; em auction.elicitacao.cloud com API em api.elicitacao.cloud, ao primeiro 502/503 tenta mesma origem (proxy)
      if (res.status === 502 || res.status === 503) {
        const origin = typeof window !== 'undefined' ? window.location.origin : '';
        const baseUrl = getBaseUrl();
        if (
          sameOriginApiFallbackEnabled() &&
          origin === 'https://auction.elicitacao.cloud' &&
          baseUrl === 'https://api.elicitacao.cloud' &&
          !_sameOriginFallbackTried
        ) {
          _sameOriginFallbackTried = true;
          _effectiveApiBase = '';
          if (typeof console !== 'undefined') {
            console.warn('[Auction API] 502/503 em api.elicitacao.cloud; tentando mesma origem (proxy).');
          }
          return request(method, path, body, options);
        }
        // Mesma origem (proxy Nginx) sem upstream ou mal configurado: tenta API pública no subdomínio (Coolify com BACKEND_UPSTREAM errado).
        if (
          origin === 'https://auction.elicitacao.cloud' &&
          baseUrl === '' &&
          !_directApiFallbackTried &&
          isNginxProxyBackendMissing(data)
        ) {
          _directApiFallbackTried = true;
          _effectiveApiBase = resolveDirectApiFallbackUrl();
          if (typeof console !== 'undefined') {
            console.warn('[Auction API] Proxy sem backend; tentando API direta:', _effectiveApiBase);
          }
          return request(method, path, body, options);
        }
        if (method === 'GET' && !options._retried) {
          options._retried = true;
          await new Promise((r) => setTimeout(r, 2000));
          return request(method, path, body, options);
        }
        const rawMsg = (data.message ?? data.error ?? res.statusText) || '';
        const isInfra = isGatewayInfraPayload(data);
        const msg = isInfra
          ? SERVICE_UNAVAILABLE_MESSAGE
          : (getFriendlyHttpErrorMessage(res.status, rawMsg, SERVICE_UNAVAILABLE_MESSAGE) || SERVICE_UNAVAILABLE_MESSAGE);
        const unavailableError = new Error(msg);
        unavailableError.status = res.status;
        unavailableError.data = data;
        unavailableError.isConnectionError = true;
        unavailableError.userMessage = isInfra
          ? SERVICE_UNAVAILABLE_MESSAGE
          : ((typeof msg === 'string' && msg.trim()) ? msg : SERVICE_UNAVAILABLE_MESSAGE);
        unavailableError.name = 'ServiceUnavailableError';
        throw unavailableError;
      }
      const err = new Error(data.message ?? data.error ?? res.statusText);
      err.status = res.status;
      err.data = data;
      err.action = data.action;
      if (res.status === 409 && method === 'POST' && path === '/api/editais') {
        err.isDuplicateEdital = true;
        err.name = 'DuplicateEditalError';
      }
      throw err;
    }
    return data;
  } catch (error) {
    // Se já foi tratado acima, re-lançar
    if (error.isForbidden || error.isUnauthorized) {
      throw error;
    }
    // Tratamento específico para erros de conexão (fetch falha: rede, SSL, CORS, timeout)
    const isConnectionError =
      error.name === 'TypeError' &&
      (error.message.includes('Failed to fetch') ||
        error.message.includes('Load failed') ||
        error.message.includes('ERR_') ||
        error.message.includes('NetworkError') ||
        error.cause?.code === 'ECONNREFUSED' ||
        error.cause?.code === 'ECONNRESET' ||
        error.cause?.code === 'CERT_HAS_EXPIRED' ||
        error.cause?.code === 'UNABLE_TO_VERIFY_LEAF_SIGNATURE');

    if (isConnectionError) {
      const debug = typeof localStorage !== 'undefined' && (localStorage.getItem('auction_debug_api') === '1' || sessionStorage.getItem('auction_debug_api') === '1');
      const origin = typeof window !== 'undefined' ? window.location.origin : '';
      const baseUrl = getBaseUrl();
      // Fallback: em auction.elicitacao.cloud com API em api.elicitacao.cloud inacessível, tenta mesma origem (proxy)
      if (origin === 'https://auction.elicitacao.cloud' && baseUrl === 'https://api.elicitacao.cloud') {
        await trySameOriginFallback();
        if (getBaseUrl() === '') {
          // Fallback ativado; refaz a requisição com mesma origem
          return request(method, path, body, options);
        }
      }
      let messageForError = CONNECTION_ERROR_USER_MESSAGE;
      if (debug) {
        const rawMsg = error.message || (error.cause && error.cause.message) || '';
        const isElicitacaoCloud = INITIAL_API_URL.includes('api.elicitacao.cloud');
        const sslHint = isElicitacaoCloud
          ? ' Possíveis causas: (1) Cache/HSTS do navegador — teste em aba anônima e limpe cache/HSTS para este domínio; (2) Antivírus/proxy com "inspeção HTTPS" — desative temporariamente para testar; (3) DNS — no hPanel confirme que api.elicitacao.cloud (A/AAAA) aponta só para o IP do VPS; (4) Se o servidor já tem certificado válido, o erro pode ser local. Confira também se o frontend está chamando api.elicitacao.cloud e não api.auction.com (CORS).'
          : ' Possíveis causas: servidor offline, SSL inválido, CORS ou firewall.';
        const prefix = isElicitacaoCloud && sslHint ? 'Depuração: ' : '';
        messageForError = `${prefix}Não foi possível conectar ao servidor em ${baseUrl || '(mesma origem)'}. Detalhe: ${rawMsg}.${sslHint}`;
      }
      const connectionError = new Error(messageForError);
      connectionError.status = 0;
      connectionError.isConnectionError = true;
      connectionError.userMessage = CONNECTION_ERROR_USER_MESSAGE;
      connectionError.name = 'ConnectionError';
      connectionError.originalError = error;
      if (typeof console !== 'undefined') {
        const pathInfo = typeof path !== 'undefined' ? { method, path, fullUrl: url } : {};
        if (!_connectionErrorWarned) {
          _connectionErrorWarned = true;
          console.error('[Auction API] Erro de conexão:', { apiUrl: baseUrl || '(mesma origem)', origin, message: error.message, cause: error.cause, ...pathInfo });
          _connectionDiagnosticRun = true;
          checkApiConnection({ suppressConsoleLog: true }).then((r) => {
            console.error('[Auction API] Diagnóstico automático:', r);
            if (r.hint) console.error('[Auction API] Dica:', r.hint);
          }).catch(() => {});
        }
      }
      throw connectionError;
    }
    // Tratamento para timeout/abort
    if (error.name === 'AbortError') {
      const timeoutError = new Error('A requisição demorou muito para responder. Tente novamente.');
      timeoutError.status = 408;
      throw timeoutError;
    }
    throw error;
  }
}

/**
 * Retorna mensagem adequada para exibir ao usuário (evita texto técnico em erros de conexão/502).
 * @param {Error & { userMessage?: string, isConnectionError?: boolean, data?: { error?: string }, status?: number }} error
 * @param {string} [fallback] Mensagem padrão quando não houver mensagem no erro.
 * @returns {string}
 */
/** Mensagem amigável para sessão expirada ou token ausente (401). */
export const SESSION_EXPIRED_MESSAGE = 'Sessão expirada. Faça login novamente.';

/** Evita exibir mensagem longa de depuração na UI (sempre troca por mensagem amigável). */
function isDebugOrConnectionHint(msg) {
  if (typeof msg !== 'string') return false;
  return msg.includes('Depuração:') || msg.includes('Possíveis causas:') || msg.includes('Não foi possível conectar ao servidor em ');
}

/** Mensagens técnicas de rede que devem ser substituídas por mensagem amigável. */
function isRawConnectionError(msg) {
  if (typeof msg !== 'string') return false;
  const lower = msg.toLowerCase();
  return lower.includes('failed to fetch') || lower.includes('load failed') ||
    lower.includes('networkerror') || lower.includes('network error') ||
    lower.includes('err_') || lower.includes('econnrefused') || lower.includes('econnreset');
}

/** Mensagem padrão para limite de taxa (429) em rotas PNCP / API. */
export const RATE_LIMIT_USER_MESSAGE =
  'Muitas consultas em pouco tempo. Aguarde cerca de um minuto e tente de novo.';

/** Quando o backend devolve stack, Prisma ou texto longo demais — não exibir na UI. */
export const GENERIC_INTERNAL_ERROR_MESSAGE =
  'Não foi possível concluir a operação no servidor. Tente de novo em instantes. Se persistir, contacte o suporte.';

export function isTechnicalErrorMessage(msg) {
  if (typeof msg !== 'string') return true;
  if (msg.length > 600) return true;
  const lower = msg.toLowerCase();
  return (
    lower.includes('prisma') ||
    lower.includes('invalid `prisma') ||
    lower.includes('invalid value provided') ||
    lower.includes('expected string') ||
    lower.includes('invocation:') ||
    lower.includes('\tat ') ||
    lower.includes('econnrefused')
  );
}

/**
 * @param {number} status
 * @param {unknown} backendMsg
 * @param {string} [fallback]
 * @returns {string}
 */
export function getFriendlyHttpErrorMessage(status, backendMsg, fallback) {
  const fb = fallback ?? GENERIC_INTERNAL_ERROR_MESSAGE;
  const raw = backendMsg != null ? String(backendMsg).trim() : '';
  if (status === 503) return SERVICE_UNAVAILABLE_MESSAGE;
  if (status === 500 || status === 502) {
    if (!raw || isTechnicalErrorMessage(raw) || isGatewayInfraErrorText(raw)) return fb;
    return raw;
  }
  if (status === 504) {
    if (!raw || isTechnicalErrorMessage(raw) || isGatewayInfraErrorText(raw)) return SERVICE_UNAVAILABLE_MESSAGE;
    return raw;
  }
  return raw || fb;
}

/** 401 no POST /auth/login (credenciais) — não confundir com sessão/token expirado. */
function isLoginCredentialFailure401(error) {
  if (error?.status !== 401 && !error?.isUnauthorized) return false;
  const api = error?.data?.error ?? error?.data?.message ?? error?.message ?? '';
  if (typeof api !== 'string') return false;
  const s = api.toLowerCase();
  return (
    s.includes('credencial') ||
    (s.includes('invalid') && s.includes('credential')) ||
    (s.includes('e-mail') && s.includes('senha')) ||
    s.includes('email e senha são obrigatórios')
  );
}

export function getDisplayMessage(error, fallback = 'Ocorreu um erro. Tente novamente.') {
  if (!error) return fallback;
  const llmCode = error?.data?.code;
  if (typeof llmCode === 'string' && llmCode.startsWith('LLM_')) {
    const m = error?.data?.message ?? error?.data?.error;
    if (typeof m === 'string' && m.trim()) return m.trim();
  }
  function appendCertifyCauseIfAny(text) {
    const c = error?.data?.cause;
    const code = error?.data?.code;
    if (typeof c !== 'string' || !c.trim()) return text;
    if (typeof code !== 'string' || !code.startsWith('CERTIFY_UPLOAD')) return text;
    return `${text} — ${c.trim().slice(0, 800)}`;
  }
  if (error.status === 502 || error.status === 503 || error.status === 504) {
    if (error?.data && isGatewayInfraPayload(error.data)) return SERVICE_UNAVAILABLE_MESSAGE;
    const raw = error?.data?.message ?? error?.data?.error ?? error?.message;
    return getFriendlyHttpErrorMessage(error.status, raw, SERVICE_UNAVAILABLE_MESSAGE);
  }
  if (error.isConnectionError && error.userMessage) return error.userMessage;
  if (error.isConnectionError) return CONNECTION_ERROR_USER_MESSAGE;
  if (error.status === 429) {
    const m = error?.data?.error ?? error?.data?.message ?? error?.message;
    if (typeof m === 'string' && m.trim() && !m.includes('429')) return m.trim();
    return RATE_LIMIT_USER_MESSAGE;
  }
  if (error?.data?.code === 'PNCP_RATE_LIMIT') {
    const m = error?.data?.error;
    return typeof m === 'string' && m.trim() ? m.trim() : RATE_LIMIT_USER_MESSAGE;
  }
  if (error.status === 500) {
    const raw = error?.data?.message ?? error?.data?.error ?? error?.message;
    const base = getFriendlyHttpErrorMessage(500, raw, fallback);
    const ref = error?.data?.correlation_id;
    const pcode = error?.data?.prisma_code;
    const bits = [];
    if (typeof ref === 'string' && ref.trim()) bits.push(`ref: ${ref.trim()}`);
    if (typeof pcode === 'string' && pcode.trim()) bits.push(`Prisma: ${pcode.trim()}`);
    const core = bits.length ? `${base} (${bits.join(', ')})` : base;
    return appendCertifyCauseIfAny(core);
  }
  if (error.status === 0) return CONNECTION_ERROR_USER_MESSAGE;
  if (isLoginCredentialFailure401(error)) {
    const m = error?.data?.error ?? error?.data?.message ?? error?.message;
    return typeof m === 'string' && m.trim() ? m.trim() : fallback;
  }
  if (error.status === 401 || error?.isUnauthorized) return SESSION_EXPIRED_MESSAGE;
  if (error?.data?.code === 'TOKEN_EXPIRED') return SESSION_EXPIRED_MESSAGE;
  if (error?.data?.code === 'EMPRESA_PENDING_APPROVAL') {
    const m = error?.data?.error ?? error?.data?.message;
    return typeof m === 'string' && m.trim() ? m.trim() : 'Seu cadastro ainda está em análise. Aguarde a liberação por e-mail.';
  }
  let msg = error?.data?.error ?? error?.data?.message ?? error?.message;
  if (typeof msg === 'string' && (msg.includes('Token não fornecido') || msg.includes('não autenticado') || msg.includes('Token expirado'))) return SESSION_EXPIRED_MESSAGE;
  if (isRawConnectionError(msg) || isDebugOrConnectionHint(msg)) return CONNECTION_ERROR_USER_MESSAGE;
  if (typeof msg === 'string' && msg.trim() && isTechnicalErrorMessage(msg)) return GENERIC_INTERNAL_ERROR_MESSAGE;
  const tail = (typeof msg === 'string' && msg.trim()) ? msg.trim() : fallback;
  return appendCertifyCauseIfAny(tail);
}

export const api = {
  get: (path, options) => request('GET', path, undefined, options),
  post: (path, body, options) => request('POST', path, body, options),
  put: (path, body, options) => request('PUT', path, body, options),
  patch: (path, body, options) => request('PATCH', path, body, options),
  delete: (path, options = {}) => {
    const { body, ...rest } = options;
    return request('DELETE', path, body, rest);
  },
};

export const suporteApi = {
  listar: (params = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v != null && String(v).trim() !== '') q.set(k, String(v));
    });
    return api.get(`/api/suporte/tickets${q.toString() ? `?${q.toString()}` : ''}`);
  },
  obter: (id) => api.get(`/api/suporte/tickets/${id}`),
  criar: (data) => api.post('/api/suporte/tickets', data),
  atualizar: (id, data) => api.patch(`/api/suporte/tickets/${id}`, data),
  excluir: (id) => api.delete(`/api/suporte/tickets/${id}`),
};

/** AuctionCertify — selagem e listagem (autenticado). Verificação pública: `certifyPublicApi`. */
export const certifyApi = {
  /** multipart AuctionCertify (teto ~1 GB por omissão na API; ficheiro em disco sob UPLOAD_DIR). Opcional: `onProgress` usa XHR. */
  uploadBlob: (file, options) => uploadCertifyFile(file, options),
  evidencesList: (query) => {
    const q =
      query != null && String(query).trim() !== ''
        ? `?q=${encodeURIComponent(String(query).trim())}`
        : '';
    return api.get(`/api/certify/evidences${q}`);
  },
  evidencesCreate: (body) => api.post('/api/certify/evidences', body),
  evidencesGet: (id) => api.get(`/api/certify/evidences/${encodeURIComponent(id)}`),
  evidencesDelete: (id) => api.delete(`/api/certify/evidences/${encodeURIComponent(id)}`),
  /** GET PDF com Bearer — usar fetch + getApiUrl por não ser JSON */
  evidencesPdfRequestUrl: (id) =>
    `${getApiUrl().replace(/\/$/, '')}/api/certify/evidences/${encodeURIComponent(id)}/pdf`,
};

/** Endpoints públicos /verify (sem JWT próprio da sessão de verificação). */
export const certifyPublicApi = {
  meta: (evidenceId) =>
    api.get(`/api/certify/public/verify/${encodeURIComponent(evidenceId)}/meta`),
  verifyWithPassword: (evidenceId, password) =>
    api.post(`/api/certify/public/verify/${encodeURIComponent(evidenceId)}`, { password }),
  detail: (evidenceId, sessionToken) =>
    api.get(`/api/certify/public/verify/${encodeURIComponent(evidenceId)}/detail`, {
      headers: { Authorization: `Bearer ${sessionToken}` },
    }),
  downloadUrl: (evidenceId, sessionToken) =>
    api.get(`/api/certify/public/verify/${encodeURIComponent(evidenceId)}/download-url`, {
      headers: { Authorization: `Bearer ${sessionToken}` },
    }),
  fileUrl: (evidenceId) =>
    `${getApiUrl().replace(/\/$/, '')}/api/certify/public/verify/${encodeURIComponent(evidenceId)}/file`,
};

/** Normaliza resposta da API (camelCase Prisma) para o formato esperado pelas telas (snake_case). */
function mapDocumentoFromApi(d) {
  if (!d || typeof d !== 'object') return d;
  const toYmd = (v) => {
    if (v == null || v === '') return '';
    const s = typeof v === 'string' ? v : String(v);
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
    return m ? m[1] : '';
  };
  // Remove camelCase duplicado: se ficar no objeto, o PATCH pode reenviar valor antigo e sobrescrever o editado.
  const {
    emailsNotificacao: _en,
    historicoAnexos: _ha,
    dataEmissao: _de,
    dataValidadeInicial: _dvi,
    dataValidadeFinal: _dvf,
    linkSolicitacao: _ls,
    anexoNome: _an,
    conteudoExtraido: _ce,
    url: _url,
    documentoTipoId: _dti,
    modoControle: _mc,
    tipoValidade: _tv,
    competenciaMesAno: _cma,
    criterioTextual: _ct,
    valorDeclarado: _vd,
    valorMinimoExigido: _vme,
    statusConformidade: _sc,
    statusOperacional: _so,
    ...rest
  } = d;
  return {
    ...rest,
    data_emissao: d.data_emissao ?? toYmd(d.dataEmissao),
    data_validade_inicial: d.data_validade_inicial ?? toYmd(d.dataValidadeInicial),
    data_validade_final: d.data_validade_final ?? toYmd(d.dataValidadeFinal),
    anexo_url: d.anexo_url ?? d.url ?? '',
    anexo_nome: d.anexo_nome ?? d.anexoNome ?? '',
    link_solicitacao: d.link_solicitacao ?? d.linkSolicitacao ?? '',
    historico_anexos: Array.isArray(d.historico_anexos)
      ? d.historico_anexos
      : Array.isArray(d.historicoAnexos)
        ? d.historicoAnexos
        : [],
    conteudo_extraido: d.conteudo_extraido ?? d.conteudoExtraido ?? '',
    documento_tipo_id: d.documento_tipo_id ?? d.documentoTipoId ?? '',
    modo_controle: d.modo_controle ?? d.modoControle ?? 'manual_controlado',
    tipo_validade: d.tipo_validade ?? d.tipoValidade ?? 'data_fixa',
    competencia_mes_ano: d.competencia_mes_ano ?? d.competenciaMesAno ?? '',
    criterio_textual: d.criterio_textual ?? d.criterioTextual ?? '',
    valor_declarado: d.valor_declarado ?? d.valorDeclarado ?? null,
    valor_minimo_exigido: d.valor_minimo_exigido ?? d.valorMinimoExigido ?? null,
    status_conformidade: d.status_conformidade ?? d.statusConformidade ?? 'pendente_analise',
    status_operacional: d.status_operacional ?? d.statusOperacional ?? 'pendente_renovacao',
    emails_notificacao: Array.isArray(d.emails_notificacao)
      ? d.emails_notificacao
      : Array.isArray(d.emailsNotificacao)
        ? d.emailsNotificacao
        : [],
    observacoes: d.observacoes ?? '',
    tipo: d.tipo ?? '',
    categoria: d.categoria ?? '',
    descricao: d.descricao ?? '',
    status: d.status ?? 'valido',
  };
}

/** Atestado/Declaração (tela CriarAtestado): Prisma camelCase → snake_case da UI. */
function mapAtestadoFromApi(d) {
  if (!d || typeof d !== 'object') return d;
  const imagensRaw = d.imagens;
  const imagens = Array.isArray(imagensRaw) ? imagensRaw : [];
  let dataFin = d.data_finalizacao ?? d.dataFinalizacao ?? null;
  if (dataFin && typeof dataFin === 'object' && typeof dataFin.toISOString === 'function') {
    dataFin = dataFin.toISOString();
  }
  return {
    id: d.id,
    edital_id: d.edital_id ?? d.editalId ?? '',
    tipo_documento: d.tipo_documento ?? d.tipoDocumento ?? 'Outros',
    titulo: d.titulo ?? d.nome ?? '',
    conteudo_html: d.conteudo_html ?? d.conteudoHtml ?? '',
    destinatario: d.destinatario ?? '',
    orgao_secretaria: d.orgao_secretaria ?? d.orgaoSecretaria ?? '',
    sistema: d.sistema ?? '',
    referencia: d.referencia ?? '',
    local_data: d.local_data ?? d.localData ?? '',
    agente_contratacao: d.agente_contratacao ?? d.agenteContratacao ?? '',
    sistema_compras: d.sistema_compras ?? d.sistemaCompras ?? '',
    imagens,
    status: d.status ?? 'rascunho',
    data_finalizacao: dataFin,
    nome: d.nome ?? '',
    descricao: d.descricao ?? '',
    created_at: d.created_at ?? d.createdAt,
    updated_at: d.updated_at ?? d.updatedAt,
  };
}

/** Código 409 ao excluir edital com vínculos (alinhado ao servidor). */
export const EDITAL_TRASH_BLOCKED_CODE = 'EDITAL_TRASH_BLOCKED';

/** GET /api/editais pode devolver array direto ou objeto com lista aninhada (gateways, versões antigas). */
function coalesceEditaisListPayload(data) {
  if (data == null) return [];
  if (Array.isArray(data)) return data;
  if (typeof data !== 'object') return [];
  if (Array.isArray(data.items)) return data.items;
  if (Array.isArray(data.data)) return data.data;
  if (Array.isArray(data.editais)) return data.editais;
  if (Array.isArray(data.results)) return data.results;
  if (Array.isArray(data.records)) return data.records;
  return [];
}

// Entidades (sempre com tenant via token)
export const entities = {
  Edital: {
    list: (orderBy = '-updatedAt', limit = 100, filters = {}) => {
      const params = new URLSearchParams({ order: orderBy, limit: String(limit) });
      if (filters.status != null) params.set('status', String(filters.status));
      if (filters.status_aprovacao != null) params.set('status_aprovacao', String(filters.status_aprovacao));
      if (filters.oculto_kanban !== undefined) params.set('oculto_kanban', String(!!filters.oculto_kanban));
      if (filters.deleted_scope != null && filters.deleted_scope !== '') {
        params.set('deleted_scope', String(filters.deleted_scope));
      }
      if (filters.view != null && String(filters.view).trim() !== '') {
        params.set('view', String(filters.view).trim());
      }
      return api.get(`/api/editais?${params}`).then((data) => coalesceEditaisListPayload(data));
    },
    get: (id) => api.get(`/api/editais/${id}`),
    /** Lookup de edital existente pelo SHA-256 do arquivo principal (200, sem 409). */
    findByContentSha256: (hash) =>
      api.get(`/api/editais/por-conteudo-sha256/${encodeURIComponent(String(hash || '').trim().toLowerCase())}`),
    /** Texto integral dos documentos persistido (cache da "Pergunte a IA"); pode retornar null. */
    getTextoDocumentosIa: (id) => api.get(`/api/editais/${id}/texto-documentos-ia`),
    /** Mapeia itens + colunas do edital para formato da proposta via IA (requer IA configurada). */
    itensParaPropostaIA: (id) => api.post(`/api/editais/${id}/itens-para-proposta-ia`, {}),
    create: (data) => api.post('/api/editais', data),
    update: (id, data) => api.patch(`/api/editais/${id}`, data),
    /** Fast-Pass: 4 identificadores via IA rápida + verificação de duplicidade semântica. */
    fastPassDuplicidade: (id, body) => api.post(`/api/editais/${id}/fast-pass`, body),
    createProcessamentoJob: (id, body) => api.post(`/api/editais/${id}/processamento-jobs`, body),
    getProcessamentoJob: (jobId) => api.get(`/api/editais/processamento-jobs/${jobId}`),
    streamProcessamentoJobEvents: (jobId) =>
      api.get(`/api/editais/processamento-jobs/${jobId}/events`, {
        headers: { Accept: 'text/event-stream' },
        responseType: 'stream',
      }),
    cancelProcessamentoJob: (jobId) => api.delete(`/api/editais/processamento-jobs/${jobId}`),
    /** @param {string} id
     *  @param {{ cascade?: boolean }} [options] — `cascade: true` envia propostas/contratos/lições à lixeira junto. */
    delete: (id, options = {}) => {
      const q = options.cascade ? '?cascade=1' : '';
      const body =
        options.motivo_exclusao != null && String(options.motivo_exclusao).trim() !== ''
          ? { motivo_exclusao: String(options.motivo_exclusao).trim() }
          : undefined;
      return api.delete(`/api/editais/${id}${q}`, { body });
    },
    /** Pré-visualização de vínculos antes de excluir (lote). */
    trashPreview: (ids) => api.post('/api/editais/trash-preview', { ids }),
    filter: (query, orderBy, limit) =>
      api.get(`/api/editais?order=${orderBy || '-updatedAt'}&limit=${limit || 100}`).then((list) =>
        coalesceEditaisListPayload(list).filter((e) => Object.entries(query || {}).every(([k, v]) => e[k] === v))
      ),
    /**
     * Listagem leve para Análise (payload reduzido) + contagens globais em uma resposta.
     * @returns {{ items: unknown[], stats: { total: number, pendente: number, aprovar_seguir: number, nao_seguir: number, favoritos: number } }}
     */
    listAnaliseResumo: (orderBy = '-createdAt', limit = 100, filters = {}) => {
      const params = new URLSearchParams({
        order: orderBy,
        limit: String(limit),
        view: 'resumo',
        stats: '1',
      });
      if (filters.status != null) params.set('status', String(filters.status));
      if (filters.status_aprovacao != null && filters.status_aprovacao !== '') {
        params.set('status_aprovacao', String(filters.status_aprovacao));
      }
      if (filters.oculto_kanban !== undefined) {
        params.set('oculto_kanban', String(!!filters.oculto_kanban));
      }
      if (filters.deleted_scope != null && filters.deleted_scope !== '') {
        params.set('deleted_scope', String(filters.deleted_scope));
      }
      const emptyStats = {
        total: 0,
        pendente: 0,
        aprovar_seguir: 0,
        nao_seguir: 0,
        favoritos: 0,
      };
      return api.get(`/api/editais?${params}`).then((data) => {
        if (
          data &&
          typeof data === 'object' &&
          Array.isArray(data.items) &&
          data.stats &&
          typeof data.stats === 'object'
        ) {
          return { items: data.items, stats: { ...emptyStats, ...data.stats } };
        }
        return { items: [], stats: emptyStats };
      });
    },
  },
  /** Módulo Manifesto — rotas aninhadas em /api/editais/:editalId/manifestacoes */
  Manifesto: {
    getFeriadosConfig: () => api.get('/api/editais/manifesto/feriados'),
    setFeriadosConfig: (feriados) => api.put('/api/editais/manifesto/feriados', { feriados }),
    listManifestacoes: (editalId) => api.get(`/api/editais/${editalId}/manifestacoes`),
    getManifestacao: (editalId, manifestacaoId) =>
      api.get(`/api/editais/${editalId}/manifestacoes/${manifestacaoId}`),
    createManifestacao: (editalId, body) => api.post(`/api/editais/${editalId}/manifestacoes`, body),
    patchManifestacao: (editalId, manifestacaoId, body) =>
      api.patch(`/api/editais/${editalId}/manifestacoes/${manifestacaoId}`, body),
    postPecaVersao: (editalId, manifestacaoId, body) =>
      api.post(`/api/editais/${editalId}/manifestacoes/${manifestacaoId}/peca`, body),
    gerarRascunhoIa: (editalId, manifestacaoId) =>
      api.post(`/api/editais/${editalId}/manifestacoes/${manifestacaoId}/gerar-rascunho-ia`, {}),
    listPrazos: (editalId, manifestacaoId) =>
      api.get(`/api/editais/${editalId}/manifestacoes/${manifestacaoId}/prazos`),
    createPrazo: (editalId, manifestacaoId, body) =>
      api.post(`/api/editais/${editalId}/manifestacoes/${manifestacaoId}/prazos`, body),
    patchPrazo: (editalId, manifestacaoId, prazoId, body) =>
      api.patch(`/api/editais/${editalId}/manifestacoes/${manifestacaoId}/prazos/${prazoId}`, body),
    deletePrazo: (editalId, manifestacaoId, prazoId) =>
      api.delete(`/api/editais/${editalId}/manifestacoes/${manifestacaoId}/prazos/${prazoId}`),
    /** PDF da última versão persistida (timbrado). */
    downloadManifestoPdf: (editalId, manifestacaoId) =>
      fetchManifestoPdf(`/api/editais/${editalId}/manifestacoes/${manifestacaoId}/pdf`),
    /** PDF timbrado com texto do rascunho (não grava versão). */
    previewManifestoPdf: (editalId, manifestacaoId, conteudo) =>
      fetchManifestoPdf(`/api/editais/${editalId}/manifestacoes/${manifestacaoId}/pdf-preview`, {
        method: 'POST',
        body: { conteudo },
      }),
  },
  Proposta: {
    list: (orderBy = '-updatedAt', limit = 100) =>
      api.get(`/api/propostas?order=${orderBy}&limit=${limit}`).then((data) => (Array.isArray(data) ? data : [])),
    get: (id) => api.get(`/api/propostas/${id}`),
    create: (data) => api.post('/api/propostas', data),
    update: (id, data) => api.patch(`/api/propostas/${id}`, data),
    versionar: (id) => api.post(`/api/propostas/${id}/versionar`, {}),
    enviarEmail: (id, data) => api.post(`/api/propostas/${id}/enviar-email`, data),
    delete: (id) => api.delete(`/api/propostas/${id}`),
  },
  Contrato: {
    list: () => api.get('/api/contratos').then((data) => (Array.isArray(data) ? data : []).map((c) => ({
      ...c,
      numero_contrato: c.numero_contrato ?? c.numeroContrato,
      orgao_contratante: c.orgao_contratante ?? c.orgaoContratante,
      numero_processo: c.numero_processo ?? c.numeroProcesso,
      data_inicio: c.data_inicio ?? c.dataInicio,
      data_fim: c.data_fim ?? c.dataFim,
      valor_total: c.valor_total ?? c.valor,
      valor_mensal_previsto: c.valor_mensal_previsto ?? c.valorMensalPrevisto,
      valor_glosado: c.valor_glosado ?? c.valorGlosado,
      forecast_mensal: c.forecast_mensal ?? c.forecastMensal,
      periodicidade_reajuste: c.periodicidade_reajuste ?? c.periodicidadeReajuste,
      forma_pagamento: c.forma_pagamento ?? c.formaPagamento,
      centro_custo: c.centro_custo ?? c.centroCusto,
      fonte_recurso: c.fonte_recurso ?? c.fonteRecurso,
      classificacao_orcamentaria: c.classificacao_orcamentaria ?? c.classificacaoOrcamentaria,
      modalidade: c.modalidade ?? null,
      tipo_contrato: c.tipo_contrato ?? c.tipoContrato ?? null,
      unidade_gestora: c.unidade_gestora ?? c.unidadeGestora ?? null,
      cnpj_cpf: c.cnpj_cpf ?? c.cnpjCpf ?? null,
      gestor_contrato: c.gestor_contrato ?? c.gestorContrato ?? null,
      responsavel_interno: c.responsavel_interno ?? c.responsavelInterno ?? null,
      multas_penalidades: c.multas_penalidades ?? c.multasPenalidades ?? null,
      clausulas_principais: c.clausulas_principais ?? c.clausulasPrincipais ?? null,
      arquivo_contrato_url: c.arquivo_contrato_url ?? c.arquivoContratoUrl ?? null,
      arquivo_contrato_nome: c.arquivo_contrato_nome ?? c.arquivoContratoNome ?? null,
      anexos: Array.isArray(c.anexos)
        ? c.anexos
        : Array.isArray(c.anexosContrato)
          ? c.anexosContrato
          : [],
    }))),
    get: (id) => api.get(`/api/contratos/${id}`).then((c) => {
      if (!c) return c;
      return {
        ...c,
        numero_contrato: c.numero_contrato ?? c.numeroContrato,
        orgao_contratante: c.orgao_contratante ?? c.orgaoContratante,
        fornecedor: c.fornecedor ?? c.fornecedor,
        numero_processo: c.numero_processo ?? c.numeroProcesso,
        data_inicio: c.data_inicio ?? c.dataInicio,
        data_fim: c.data_fim ?? c.dataFim,
        valor_total: c.valor_total ?? c.valor,
        valor_mensal_previsto: c.valor_mensal_previsto ?? c.valorMensalPrevisto,
        valor_faturado: c.valor_faturado ?? c.valorFaturado,
        valor_glosado: c.valor_glosado ?? c.valorGlosado,
        periodicidade_reajuste: c.periodicidade_reajuste ?? c.periodicidadeReajuste,
        forma_pagamento: c.forma_pagamento ?? c.formaPagamento,
        centro_custo: c.centro_custo ?? c.centroCusto,
        fonte_recurso: c.fonte_recurso ?? c.fonteRecurso,
        classificacao_orcamentaria: c.classificacao_orcamentaria ?? c.classificacaoOrcamentaria,
        modalidade: c.modalidade ?? null,
        tipo_contrato: c.tipo_contrato ?? c.tipoContrato ?? null,
        unidade_gestora: c.unidade_gestora ?? c.unidadeGestora ?? null,
        cnpj_cpf: c.cnpj_cpf ?? c.cnpjCpf ?? null,
        gestor_contrato: c.gestor_contrato ?? c.gestorContrato ?? null,
        responsavel_interno: c.responsavel_interno ?? c.responsavelInterno ?? null,
        multas_penalidades: c.multas_penalidades ?? c.multasPenalidades ?? null,
        clausulas_principais: c.clausulas_principais ?? c.clausulasPrincipais ?? null,
        arquivo_contrato_url: c.arquivo_contrato_url ?? c.arquivoContratoUrl ?? null,
        arquivo_contrato_nome: c.arquivo_contrato_nome ?? c.arquivoContratoNome ?? null,
        anexos: Array.isArray(c.anexos)
          ? c.anexos
          : Array.isArray(c.anexosContrato)
            ? c.anexosContrato
            : [],
      };
    }),
    create: (data) => api.post('/api/contratos', data),
    update: (id, data) => api.patch(`/api/contratos/${id}`, data),
    delete: (id) => api.delete(`/api/contratos/${id}`),
    preencherFromEdital: (id) => api.post(`/api/contratos/${id}/preencher-from-edital`),
  },
  Documento: {
    list: () =>
      api.get('/api/documentos').then((list) => {
        if (!Array.isArray(list)) return [];
        const mapped = list.map(mapDocumentoFromApi);
        return mapped.sort(
          (a, b) =>
            new Date(b.data_validade_final || b.dataValidadeFinal || 0) -
            new Date(a.data_validade_final || a.dataValidadeFinal || 0)
        );
      }),
    get: (id) => api.get(`/api/documentos/${id}`).then(mapDocumentoFromApi),
    create: (data) => api.post('/api/documentos', data),
    update: (id, data) => api.patch(`/api/documentos/${id}`, data),
    delete: (id) => api.delete(`/api/documentos/${id}`),
    notificarVencimentos: () => api.post('/api/documentos/notificar-vencimentos'),
    sincronizarStatus: () => api.post('/api/documentos/sincronizar-status'),
    tiposCatalogo: () =>
      api.get('/api/documentos/tipos-catalogo').then((data) => (Array.isArray(data) ? data : [])),
    renovacaoConfigs: () =>
      api.get('/api/documentos/renovacao/configuracoes').then((data) => (Array.isArray(data) ? data : [])),
    atualizarRenovacaoConfig: (documentoTipoId, payload) =>
      api.put(`/api/documentos/renovacao/configuracoes/${encodeURIComponent(documentoTipoId)}`, payload),
    renovacaoExecucoes: (limit = 50) =>
      api.get(`/api/documentos/renovacao/execucoes?limit=${encodeURIComponent(String(limit))}`).then((data) => (Array.isArray(data) ? data : [])),
    reprocessarRenovacaoExecucao: (id) =>
      api.post(`/api/documentos/renovacao/execucoes/${encodeURIComponent(id)}/reprocessar`, {}),
    dispararRenovacaoAutomatica: () => api.post('/api/documentos/renovacao/disparar', {}),
  },
  Company: {
    list: () => api.get('/empresas/minha-empresa').then((e) => (e ? [e] : [])),
    get: (id) => api.get('/empresas/minha-empresa').then((e) => (e?.id === id ? e : null)),
  },
  ConfiguracaoEmpresa: {
    list: () => api.get('/api/configuracao-empresa'),
    create: (data) => api.post('/api/configuracao-empresa', data),
    update: (id, data) => api.put('/api/configuracao-empresa', data),
  },
  TemplateContrato: {
    list: () => api.get('/api/template-contrato'),
    get: (id) => api.get(`/api/template-contrato/${id}`),
    create: (data) => api.post('/api/template-contrato', data),
    update: (id, data) => api.patch(`/api/template-contrato/${id}`, data),
    delete: (id) => api.delete(`/api/template-contrato/${id}`),
    gerar: (templateId) => api.post('/api/template-contrato/gerar', { template_id: templateId }),
  },
  AgendaEvent: {
    list: (orderBy = '-data_inicio') =>
      api.get('/api/agenda-events').then((list) =>
        Array.isArray(list) ? list.sort((a, b) => new Date(a.dataInicio || a.data_inicio) - new Date(b.dataInicio || b.data_inicio)) : []
      ),
    create: (data) => api.post('/api/agenda-events', data),
    update: (id, data) => api.patch(`/api/agenda-events/${id}`, data),
    delete: (id) => api.delete(`/api/agenda-events/${id}`),
  },
  PortalLicitacao: {
    list: (filters = {}) => {
      const params = new URLSearchParams();
      if (filters.q && String(filters.q).trim()) params.set('q', String(filters.q).trim());
      if (filters.favorito) params.set('favorito', '1');
      const qs = params.toString();
      const path = qs ? `/api/portais-licitacao?${qs}` : '/api/portais-licitacao';
      return api.get(path).then((data) => (Array.isArray(data) ? data : []));
    },
    create: (data) => api.post('/api/portais-licitacao', data),
    update: (id, data) => api.patch(`/api/portais-licitacao/${id}`, data),
    delete: (id) => api.delete(`/api/portais-licitacao/${id}`),
  },
  LinkImportante: {
    list: (filters = {}) => {
      const params = new URLSearchParams();
      if (filters.q && String(filters.q).trim()) params.set('q', String(filters.q).trim());
      if (filters.favorito) params.set('favorito', '1');
      const qs = params.toString();
      const path = qs ? `/api/links-importante?${qs}` : '/api/links-importante';
      return api.get(path).then((data) => (Array.isArray(data) ? data : []));
    },
    create: (data) => api.post('/api/links-importante', data),
    update: (id, data) => api.patch(`/api/links-importante/${id}`, data),
    delete: (id) => api.delete(`/api/links-importante/${id}`),
  },
  Aditivo: {
    list: () => api.get('/api/aditivos').then((data) => (Array.isArray(data) ? data : [])),
    filter: (query) =>
      (query?.contrato_id
        ? api.get(`/api/aditivos?contrato_id=${encodeURIComponent(query.contrato_id)}`)
        : api.get('/api/aditivos')).then((data) => (Array.isArray(data) ? data : [])),
    create: (data) => api.post('/api/aditivos', data),
    update: (id, data) => api.patch(`/api/aditivos/${id}`, data),
    delete: (id) => api.delete(`/api/aditivos/${id}`),
  },
  Faturamento: {
    list: () => api.get('/api/faturamentos').then((data) => (Array.isArray(data) ? data : [])),
    filter: (query) =>
      (query?.contrato_id
        ? api.get(`/api/faturamentos?contrato_id=${encodeURIComponent(query.contrato_id)}`)
        : api.get('/api/faturamentos')).then((data) => (Array.isArray(data) ? data : [])),
    get: (id) => api.get(`/api/faturamentos/${id}`),
    create: (data) => api.post('/api/faturamentos', data),
    update: (id, data) => api.patch(`/api/faturamentos/${id}`, data),
    delete: (id) => api.delete(`/api/faturamentos/${id}`),
  },
  OrdemServico: {
    list: () => api.get('/api/ordens-servico').then((data) => (Array.isArray(data) ? data : [])),
    filter: (query) =>
      (query?.contrato_id
        ? api.get(`/api/ordens-servico?contrato_id=${encodeURIComponent(query.contrato_id)}`)
        : api.get('/api/ordens-servico')).then((data) => (Array.isArray(data) ? data : [])),
    create: (data) => api.post('/api/ordens-servico', data),
    update: (id, data) => api.patch(`/api/ordens-servico/${id}`, data),
    delete: (id) => api.delete(`/api/ordens-servico/${id}`),
  },
  AtestadoTecnico: {
    list: (orderBy = '-createdAt') => api.get('/api/atestados-tecnicos'),
    get: (id) => api.get(`/api/atestados-tecnicos/${id}`),
    create: (data) => api.post('/api/atestados-tecnicos', data),
    update: (id, data) => api.patch(`/api/atestados-tecnicos/${id}`, data),
    delete: (id) => api.delete(`/api/atestados-tecnicos/${id}`),
  },
  /** Módulo Validação de Planilha — planilhas enviadas para revisão por um validador designado. */
  PlanilhaValidacao: {
    /** Lista planilhas de um edital (ordenadas por data de upload desc no backend). */
    filter: (editalId) =>
      api
        .get(`/api/planilhas-validacao?edital_id=${encodeURIComponent(editalId)}`)
        .then((d) => (Array.isArray(d) ? d : [])),
    get: (id) => api.get(`/api/planilhas-validacao/${id}`),
    create: (data) => api.post('/api/planilhas-validacao', data),
    update: (id, data) => api.patch(`/api/planilhas-validacao/${id}`, data),
    delete: (id) => api.delete(`/api/planilhas-validacao/${id}`),
  },
  User: {
    list: async () => {
      try {
        const data = await empresasApi.usuarios.list();
        return Array.isArray(data) ? data : [];
      } catch (error) {
        // Retornar array vazio silenciosamente se for erro 403 (sem permissão)
        // Isso evita que o erro seja propagado e apareça no console
        if (error?.status === 403 || error?.isForbidden) {
          // Retornar array vazio sem propagar o erro
          return [];
        }
        throw error;
      }
    },
    get: async (id) => {
      try {
        const list = await entities.User.list();
        return list.find((u) => u.id === id) ?? null;
      } catch (error) {
        if (error?.status === 403 || error?.isForbidden) {
          return null;
        }
        throw error;
      }
    },
    update: (id, data) => empresasApi.usuarios.update(id, data),
  },
  KanbanConfig: {
    list: () => invokeKanbanConfig({ action: 'load' }).then((r) => (Array.isArray(r?.configs) ? r.configs : [])),
  },
  LicaoAprendida: {
    list: (orderBy = '-createdAt') =>
      api.get(`/api/licoes-aprendidas?order=${orderBy}`).then((data) => {
        const list = Array.isArray(data) ? data : [];
        return list.map((item) => ({
          id: item.id,
          empresa_id: item.empresa_id ?? item.empresaId,
          titulo: item.titulo,
          conteudo: item.conteudo,
          edital_id: item.edital_id ?? item.editalId ?? '',
          instituicao: item.instituicao ?? '',
          edital_nome: item.edital_nome ?? item.editalNome ?? '',
          data_pregao: item.data_pregao ?? (item.dataPregao ? (typeof item.dataPregao === 'string' ? item.dataPregao : item.dataPregao.toISOString?.().split('T')[0]) : ''),
          classificacao: item.classificacao ?? '',
          categoria: item.categoria ?? '',
          vencedor: item.vencedor ?? '',
          valor_nossa_empresa: item.valor_nossa_empresa ?? item.valorNossaEmpresa ?? '',
          valor_vencedor: item.valor_vencedor ?? item.valorVencedor ?? '',
          fatos_relevantes: item.fatos_relevantes ?? item.fatosRelevantes ?? '',
          licoes_aprendidas: item.licoes_aprendidas ?? item.licoesAprendidas ?? '',
          observacoes: item.observacoes ?? '',
          created_at: item.created_at ?? item.createdAt,
          updated_at: item.updated_at ?? item.updatedAt,
        }));
      }),
    create: (data) => api.post('/api/licoes-aprendidas', data),
    update: (id, data) => api.patch(`/api/licoes-aprendidas/${id}`, data),
    delete: (id) => api.delete(`/api/licoes-aprendidas/${id}`),
  },
  Atestado: {
    list: (opts = {}) => {
      const limit = opts?.limit != null ? Number(opts.limit) : null;
      const q =
        Number.isFinite(limit) && limit > 0
          ? `?limit=${Math.min(500, Math.floor(limit))}`
          : '';
      return api.get(`/api/atestados${q}`).then((data) => {
        const arr = Array.isArray(data) ? data : [];
        return arr.map(mapAtestadoFromApi);
      });
    },
    get: (id) => api.get(`/api/atestados/${encodeURIComponent(id)}`).then(mapAtestadoFromApi),
    create: (data) => api.post('/api/atestados', data).then(mapAtestadoFromApi),
    update: (id, data) => api.patch(`/api/atestados/${encodeURIComponent(id)}`, data).then(mapAtestadoFromApi),
    delete: (id) => api.delete(`/api/atestados/${encodeURIComponent(id)}`),
  },
  ActivityLog: {
    list: (params = {}) => {
      const search = new URLSearchParams();
      Object.entries(params).forEach(([k, v]) => {
        if (v != null && v !== '') search.set(k, String(v));
      });
      const q = search.toString();
      return api.get(`/api/activity-logs${q ? `?${q}` : ''}`).then((res) => ({ data: res?.data ?? [], total: res?.total ?? 0, limit: res?.limit ?? 50, offset: res?.offset ?? 0 }));
    },
  },
  // Módulo Social
  SocialCategoria: {
    list: () => api.get('/api/social/categorias').then((data) => (Array.isArray(data) ? data : [])),
    create: (data) => api.post('/api/social/categorias', data),
    update: (id, data) => api.patch(`/api/social/categorias/${id}`, data),
    delete: (id) => api.delete(`/api/social/categorias/${id}`),
  },
  SocialPublicacao: {
    list: (categoriaId) =>
      api.get('/api/social/publicacoes' + (categoriaId ? `?categoriaId=${encodeURIComponent(categoriaId)}` : '')).then((data) => (Array.isArray(data) ? data : [])),
    get: (id) => api.get(`/api/social/publicacoes/${id}`),
    create: (data) => api.post('/api/social/publicacoes', data),
    update: (id, data) => api.patch(`/api/social/publicacoes/${id}`, data),
    delete: (id) => api.delete(`/api/social/publicacoes/${id}`),
    curtir: (id) => api.post(`/api/social/publicacoes/${id}/curtir`),
    comentarios: {
      list: (id) => api.get(`/api/social/publicacoes/${id}/comentarios`).then((data) => (Array.isArray(data) ? data : [])),
      add: (id, data) => api.post(`/api/social/publicacoes/${id}/comentarios`, data),
      delete: (id, comentarioId) => api.delete(`/api/social/publicacoes/${id}/comentarios/${comentarioId}`),
    },
  },
  SocialComunicado: {
    list: () => api.get('/api/social/comunicados').then((data) => (Array.isArray(data) ? data : [])),
    get: (id) => api.get(`/api/social/comunicados/${id}`),
    create: (data, imagemFile = null) => {
      if (imagemFile && imagemFile instanceof File) {
        const fd = new FormData();
        fd.append('titulo', data.titulo ?? '');
        fd.append('corpo', data.corpo ?? '');
        fd.append('imagem', imagemFile);
        if (data.publico != null) fd.append('publico', typeof data.publico === 'string' ? data.publico : JSON.stringify(data.publico));
        if (data.exigeConfirmacao !== undefined) fd.append('exigeConfirmacao', String(!!data.exigeConfirmacao));
        if (data.enviarEmail !== undefined) fd.append('enviarEmail', String(!!data.enviarEmail));
        return api.post('/api/social/comunicados', fd, { headers: {} });
      }
      return api.post('/api/social/comunicados', data);
    },
    update: (id, data, imagemFile = null) => {
      if (imagemFile && imagemFile instanceof File) {
        const fd = new FormData();
        if (data.titulo !== undefined) fd.append('titulo', data.titulo);
        if (data.corpo !== undefined) fd.append('corpo', data.corpo);
        fd.append('imagem', imagemFile);
        if (data.publico !== undefined) fd.append('publico', typeof data.publico === 'string' ? data.publico : JSON.stringify(data.publico));
        if (data.exigeConfirmacao !== undefined) fd.append('exigeConfirmacao', String(!!data.exigeConfirmacao));
        if (data.enviarEmail !== undefined) fd.append('enviarEmail', String(!!data.enviarEmail));
        return api.patch(`/api/social/comunicados/${id}`, fd, { headers: {} });
      }
      return api.patch(`/api/social/comunicados/${id}`, data);
    },
    delete: (id) => api.delete(`/api/social/comunicados/${id}`),
    confirmar: (id) => api.post(`/api/social/comunicados/${id}/confirmar`),
    metricas: (id) => api.get(`/api/social/comunicados/${id}/metricas`),
    relatorio: () => api.get('/api/social/comunicados/relatorio').then((data) => (Array.isArray(data) ? data : [])),
  },
  SocialPolitica: {
    list: (params = {}) => {
      const q = new URLSearchParams(params).toString();
      return api.get('/api/social/politicas' + (q ? `?${q}` : '')).then((data) => (Array.isArray(data) ? data : []));
    },
    get: (id) => api.get(`/api/social/politicas/${id}`),
    create: (data, arquivoFile = null) => {
      if (arquivoFile && arquivoFile instanceof File) {
        const fd = new FormData();
        fd.append('titulo', data.titulo ?? '');
        if (data.setor != null) fd.append('setor', data.setor);
        if (data.cargo != null) fd.append('cargo', data.cargo);
        if (data.categoria != null) fd.append('categoria', data.categoria);
        fd.append('arquivo', arquivoFile);
        if (data.permitirDownload !== undefined) fd.append('permitirDownload', String(!!data.permitirDownload));
        return api.post('/api/social/politicas', fd, { headers: {} });
      }
      return api.post('/api/social/politicas', data);
    },
    update: (id, data, arquivoFile = null) => {
      if (arquivoFile && arquivoFile instanceof File) {
        const fd = new FormData();
        if (data.titulo !== undefined) fd.append('titulo', data.titulo);
        if (data.setor !== undefined) fd.append('setor', data.setor ?? '');
        if (data.cargo !== undefined) fd.append('cargo', data.cargo ?? '');
        if (data.categoria !== undefined) fd.append('categoria', data.categoria ?? '');
        fd.append('arquivo', arquivoFile);
        if (data.permitirDownload !== undefined) fd.append('permitirDownload', String(!!data.permitirDownload));
        return api.patch(`/api/social/politicas/${id}`, fd, { headers: {} });
      }
      return api.patch(`/api/social/politicas/${id}`, data);
    },
    delete: (id) => api.delete(`/api/social/politicas/${id}`),
    confirmar: (id) => api.post(`/api/social/politicas/${id}/confirmar`),
    metricas: (id) => api.get(`/api/social/politicas/${id}/metricas`),
  },
  /** Módulo Assina — assinatura eletrônica (envelopes no backend). */
  Assina: {
    /** @param {{ page?: number, limit?: number, q?: string }} [opts] */
    list: (opts = {}) => {
      const params = new URLSearchParams();
      if (opts.page != null && opts.page > 0) params.set('page', String(opts.page));
      if (opts.limit != null && opts.limit > 0) params.set('limit', String(opts.limit));
      if (opts.q != null && String(opts.q).trim() !== '') params.set('q', String(opts.q).trim());
      const qs = params.toString();
      return api.get(`/api/assina/envelopes${qs ? `?${qs}` : ''}`).then((data) => {
        if (data && Array.isArray(data.items)) {
          return {
            items: data.items,
            total: typeof data.total === 'number' ? data.total : data.items.length,
            page: typeof data.page === 'number' ? data.page : 1,
            pageSize: typeof data.pageSize === 'number' ? data.pageSize : 10,
          };
        }
        if (Array.isArray(data)) {
          return { items: data, total: data.length, page: 1, pageSize: data.length || 10 };
        }
        return { items: [], total: 0, page: 1, pageSize: 10 };
      });
    },
    get: (id) => api.get(`/api/assina/envelopes/${id}`),
    create: (data) => api.post('/api/assina/envelopes', data),
    addDocument: (envelopeId, upload_blob_id) =>
      api.post(`/api/assina/envelopes/${envelopeId}/documentos`, { upload_blob_id }),
    removeDocument: (envelopeId, documentoId) =>
      api.delete(`/api/assina/envelopes/${envelopeId}/documentos/${documentoId}`),
    addSigners: (envelopeId, signatarios) =>
      api.post(`/api/assina/envelopes/${envelopeId}/signatarios`, { signatarios }),
    send: (envelopeId) => api.post(`/api/assina/envelopes/${envelopeId}/enviar`),
    resendInvites: (envelopeId) =>
      api.post(`/api/assina/envelopes/${envelopeId}/reenviar-convites`),
    resendCompletionEmails: (envelopeId) =>
      api.post(`/api/assina/envelopes/${envelopeId}/reenviar-email-conclusao`),
    cancel: (envelopeId) => api.post(`/api/assina/envelopes/${envelopeId}/cancelar`),
    audit: (envelopeId) => api.get(`/api/assina/envelopes/${envelopeId}/auditoria`),
  },
};

// Kanban: compatível com invoke('manageKanbanConfig', { action, company_id, newColumns })
export async function invokeKanbanConfig(payload) {
  const { action, newColumns } = payload || {};
  return api.post('/api/kanban', { action, newColumns });
}

// Upload: multipart form-data
export async function uploadFile(file) {
  const formData = new FormData();
  formData.append('file', file);
  const token = getToken();
  const res = await fetch(`${getBaseUrl()}/api/upload`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    credentials: 'include',
    mode: 'cors',
    body: formData,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw Object.assign(new Error(data.error || res.statusText), { status: res.status, data });
  return data;
}

/**
 * Upload AuctionCertify via XHR (progresso de envio). Mesma rota que `fetch`.
 * @param {File} file
 * @param {{ onProgress?: (ev: { loaded: number; total: number }) => void; signal?: AbortSignal }} [options]
 */
function uploadCertifyFileViaXhr(file, options = {}) {
  const { onProgress, signal } = options;
  const formData = new FormData();
  formData.append('file', file);
  const token = getToken();
  const base = String(getBaseUrl() || '').replace(/\/$/, '');
  const path = '/api/certify/upload';
  const url = base ? `${base}${path}` : path;
  const timeoutMs = 3 * 60 * 60 * 1000;

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    let timer = setTimeout(() => xhr.abort(), timeoutMs);

    const cleanup = () => {
      clearTimeout(timer);
      if (signal && onAbort) signal.removeEventListener('abort', onAbort);
    };

    function onAbort() {
      xhr.abort();
    }

    if (signal) {
      if (signal.aborted) {
        clearTimeout(timer);
        reject(Object.assign(new Error('Upload cancelado'), { name: 'AbortError' }));
        return;
      }
      signal.addEventListener('abort', onAbort);
    }

    xhr.upload.addEventListener('progress', (e) => {
      if (!onProgress) return;
      const total = e.lengthComputable && e.total > 0 ? e.total : file.size || 0;
      onProgress({ loaded: e.loaded, total: total > 0 ? total : Math.max(e.loaded, 1) });
    });

    xhr.addEventListener('load', () => {
      cleanup();
      let data = {};
      try {
        data = JSON.parse(xhr.responseText || '{}');
      } catch {
        data = {};
      }
      const hdrCorr = xhr.getResponseHeader('X-Correlation-Id');
      if (hdrCorr && !data.correlation_id) data = { ...data, correlation_id: hdrCorr };
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(data);
      } else {
        reject(
          Object.assign(new Error(data.error || data.message || xhr.statusText || 'Erro no upload'), {
            status: xhr.status,
            data,
          })
        );
      }
    });

    xhr.addEventListener('error', () => {
      cleanup();
      reject(new Error('Falha de rede no upload'));
    });

    xhr.addEventListener('abort', () => {
      cleanup();
      reject(Object.assign(new Error('Upload cancelado ou tempo esgotado'), { name: 'AbortError' }));
    });

    xhr.open('POST', url);
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.withCredentials = true;
    xhr.send(formData);
  });
}

/**
 * Upload AuctionCertify (teto configurável na API, ~1 GB por omissão; persistência em disco); não usar para outros módulos.
 * Com `onProgress`, usa XHR (o `fetch` não expõe progresso de envio multipart).
 * @param {File} file
 * @param {{ onProgress?: (ev: { loaded: number; total: number }) => void; signal?: AbortSignal }} [options]
 */
export async function uploadCertifyFile(file, options = {}) {
  const { onProgress } = options;
  if (typeof onProgress === 'function') {
    return uploadCertifyFileViaXhr(file, options);
  }

  const formData = new FormData();
  formData.append('file', file);
  const token = getToken();
  const controller = new AbortController();
  const timeoutMs = 3 * 60 * 60 * 1000;
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(`${getBaseUrl()}/api/certify/upload`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: 'include',
      mode: 'cors',
      body: formData,
      signal: controller.signal,
    });
    const rawText = await res.text();
    let data = {};
    try {
      data = JSON.parse(rawText || '{}');
    } catch {
      data = { error: (rawText || res.statusText || '').slice(0, 500) };
    }
    const hdrCorr = res.headers.get('X-Correlation-Id');
    const merged = hdrCorr && !data.correlation_id ? { ...data, correlation_id: hdrCorr } : data;
    if (!res.ok) {
      throw Object.assign(new Error(merged.error || merged.message || res.statusText), {
        status: res.status,
        data: merged,
      });
    }
    return merged;
  } finally {
    clearTimeout(timer);
  }
}

/** Extrai o UUID do UploadBlob a partir de `file_url` retornado por POST /api/upload. */
export function parseUploadBlobIdFromFileUrl(fileUrl) {
  if (!fileUrl || typeof fileUrl !== 'string') return null;
  const m = fileUrl.match(/\/api\/upload\/([0-9a-f-]{36})/i);
  return m ? m[1] : null;
}

/** Verifica se a URL é do endpoint de upload da nossa API (exige token para GET). */
export function isUploadUrl(url) {
  if (!url || typeof url !== 'string' || !url.includes('/api/upload/')) return false;
  const base = getBaseUrl();
  const normalized = base ? base.replace(/\/$/, '') : '';
  if (normalized && url.startsWith(normalized)) return true;
  if (normalized) {
    try {
      const baseOrigin = new URL(normalized).origin;
      const u = new URL(url, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
      if (u.pathname.includes('/api/upload/') && u.origin === baseOrigin) return true;
    } catch {
      // ignore
    }
  }
  // Mesma origem (ex.: proxy /api) ou caminho relativo — mesmo com VITE_API_URL apontando para outro host
  if (typeof window !== 'undefined') {
    if (url.startsWith('/')) return true;
    if (url.startsWith(window.location.origin)) return true;
  }
  // URL absoluta para outro host: ex. https://api.elicitacao.cloud/... quando o front usa mesma origem (VITE_API_URL vazio)
  try {
    const u = new URL(
      url,
      typeof window !== 'undefined' ? window.location.origin : 'http://localhost'
    );
    if (!u.pathname.includes('/api/upload/')) return false;
    const envApi = import.meta.env.VITE_API_URL;
    if (envApi && typeof envApi === 'string' && envApi.trim()) {
      const h = new URL(envApi.trim().replace(/\/$/, '')).hostname;
      if (u.hostname === h) return true;
    }
    if (
      u.hostname === 'api.elicitacao.cloud' ||
      u.hostname === 'api.auction.com' ||
      u.hostname === 'localhost' ||
      u.hostname === '127.0.0.1'
    ) {
      return true;
    }
  } catch {
    return false;
  }
  return false;
}

/** URL de blob persistido (GET /api/upload/:id com Bearer) — inclui casos em que `isUploadUrl` era falso antes do ajuste de rotas. */
export function requiresUploadBlobAuth(url) {
  return isUploadUrl(url) || Boolean(parseUploadBlobIdFromFileUrl(url));
}

/** Rotas GET que servem PDF por id de entidade (não confundir com GET /api/upload/:blobUuid). */
function isEntityAnexoDownloadUrl(resolved) {
  if (!resolved || typeof resolved !== 'string') return false;
  let pathname = resolved;
  let searchParams = null;
  try {
    const u = new URL(resolved, 'https://placeholder.invalid');
    pathname = u.pathname;
    searchParams = u.searchParams;
  } catch {
    pathname = resolved.split('?')[0].split('#')[0];
  }
  const uuidSeg =
    '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';
  const reUpload = new RegExp(`^/api/upload/(atestado|documento)/${uuidSeg}/?$`, 'i');
  const reAlias = new RegExp(`^/api/(atestado-anexo|documento-anexo)/${uuidSeg}/?$`, 'i');
  const reAt = new RegExp(`^/api/atestados-tecnicos/${uuidSeg}/anexo/?$`, 'i');
  const reDoc = new RegExp(`^/api/documentos/${uuidSeg}/anexo/?$`, 'i');
  if (reUpload.test(pathname) || reAlias.test(pathname) || reAt.test(pathname) || reDoc.test(pathname)) {
    return true;
  }
  const dl =
    searchParams &&
    (searchParams.get('download') === '1' || searchParams.get('anexo') === '1');
  if (dl) {
    if (new RegExp(`^/api/atestados-tecnicos/${uuidSeg}/?$`, 'i').test(pathname)) return true;
    if (new RegExp(`^/api/documentos/${uuidSeg}/?$`, 'i').test(pathname)) return true;
  }
  return false;
}

async function fetchAnexoBinaryWithBearer(url) {
  const token = getToken();
  const res = await fetch(url, {
    method: 'GET',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    credentials: 'include',
    mode: 'cors',
  });
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const j = await res.clone().json();
      if (j && typeof j.error === 'string') detail = j.error;
    } catch {
      try {
        const txt = await res.text();
        if (txt) detail = txt.slice(0, 200);
      } catch {
        /* ignore */
      }
    }
    throw Object.assign(new Error(`Falha ao baixar anexo (${res.status}): ${detail}`), {
      status: res.status,
    });
  }
  return res.blob();
}

/** Busca o arquivo de /api/upload/:id com token e retorna o Blob. */
export async function fetchUploadBlob(fileUrl) {
  const resolvedEarly = typeof fileUrl === 'string' ? resolveAttachmentFetchUrl(fileUrl) : '';
  const target = String(resolvedEarly || fileUrl || '').trim();
  if (target && isEntityAnexoDownloadUrl(target)) {
    return fetchAnexoBinaryWithBearer(target);
  }
  const token = getToken();
  const id = parseUploadBlobIdFromFileUrl(fileUrl);
  const base = getBaseUrl();
  let url;
  if (id && base) {
    url = `${base.replace(/\/$/, '')}/api/upload/${id}`;
  } else if (fileUrl.startsWith('/')) {
    const b = base?.replace(/\/$/, '') || '';
    if (b && (fileUrl.startsWith('/uploads/') || fileUrl.startsWith('/api/upload/'))) {
      url = `${b}${fileUrl}`;
    } else if (typeof window !== 'undefined') {
      url = window.location.origin + fileUrl;
    } else {
      url = fileUrl;
    }
  } else {
    url = fileUrl;
  }
  const res = await fetch(url, {
    method: 'GET',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    credentials: 'include',
    mode: 'cors',
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw Object.assign(new Error(data.error || res.statusText), { status: res.status, data });
  }
  return res.blob();
}

/**
 * URL absoluta para GET de anexo servido pela API (`/uploads/…` estático ou `/api/upload/:id`).
 * Caminhos relativos (`/uploads/foo.pdf`) não podem ser buscados na origem do SPA — causam 404 no front.
 */
export function resolveAttachmentFetchUrl(rawUrl) {
  if (!rawUrl || typeof rawUrl !== 'string') return '';
  const t = rawUrl.trim();
  const base = getBaseUrl()?.replace(/\/$/, '') || '';
  let originFromBase = '';
  if (base) {
    try {
      originFromBase = new URL(base).origin;
    } catch {
      originFromBase = '';
    }
  }

  if (t.startsWith('http://') || t.startsWith('https://')) {
    try {
      const u = new URL(t);
      if (originFromBase && (u.pathname.startsWith('/uploads/') || u.pathname.includes('/api/upload/'))) {
        return `${originFromBase}${u.pathname}${u.search}`;
      }
    } catch {
      return t;
    }
    return t;
  }

  if (t.startsWith('/uploads/') || t.startsWith('/api/upload/')) {
    if (base) return `${base}${t}`;
    if (typeof window !== 'undefined') return `${window.location.origin}${t}`;
    return t;
  }

  if (t.startsWith('uploads/')) {
    if (base) return `${base}/${t}`;
    if (typeof window !== 'undefined') return `${window.location.origin}/${t}`;
    return t;
  }

  if (t.startsWith('/')) {
    if (base) return `${base}${t}`;
    if (typeof window !== 'undefined') return `${window.location.origin}${t}`;
    return t;
  }

  return t;
}

const UUID_IN_UPLOAD_PATH = /\/api\/upload\/[0-9a-f-]{36}/i;

function extractUrlsFromHistoricoAnexos(historico) {
  if (!Array.isArray(historico)) return [];
  const out = [];
  for (const item of historico) {
    if (typeof item === 'string') {
      const t = item.trim();
      if (t) out.push(t);
    } else if (item && typeof item === 'object') {
      const u = item.url ?? item.anexo_url ?? item.anexoUrl ?? item.file_url;
      if (typeof u === 'string' && u.trim()) out.push(u.trim());
    }
  }
  return out;
}

function extractUrlsFromAnexosContrato(anexos) {
  if (!Array.isArray(anexos)) return [];
  const out = [];
  for (const item of anexos) {
    if (!item || typeof item !== 'object') continue;
    const u = item.anexo_url ?? item.anexoUrl ?? item.url ?? item.file_url;
    if (typeof u === 'string' && u.trim()) out.push(u.trim());
  }
  return out;
}

/**
 * URLs candidatas para download: primeiro rota autenticada `.../:id/anexo` (resolve UploadBlob no servidor),
 * depois `/api/upload/:uuid`, por fim URLs legadas.
 * @param {string} [primaryUrl]
 * @param {{
 *   historicoAnexos?: unknown[],
 *   historico_anexos?: unknown[],
 *   anexosContrato?: unknown[],
 *   anexos_contrato?: unknown[],
 *   atestadoTecnicoId?: string,
 *   documentoId?: string,
 * }} [options]
 */
export function collectAnexoDownloadCandidates(primaryUrl, options = {}) {
  const historicoAnexos = options.historicoAnexos ?? options.historico_anexos;
  const anexosContrato = options.anexosContrato ?? options.anexos_contrato;
  const atestadoTecnicoId = options.atestadoTecnicoId;
  const documentoId = options.documentoId;
  const base = getBaseUrl()?.replace(/\/$/, '') || '';
  const proxyUrls = [];
  if (base && atestadoTecnicoId && String(atestadoTecnicoId).trim()) {
    const aid = String(atestadoTecnicoId).trim();
    proxyUrls.push(`${base}/api/atestados-tecnicos/${aid}?download=1`);
    proxyUrls.push(`${base}/api/upload/atestado/${aid}`);
    proxyUrls.push(`${base}/api/atestado-anexo/${aid}`);
    proxyUrls.push(`${base}/api/atestados-tecnicos/${aid}/anexo`);
  }
  if (base && documentoId && String(documentoId).trim()) {
    const did = String(documentoId).trim();
    proxyUrls.push(`${base}/api/documentos/${did}?download=1`);
    proxyUrls.push(`${base}/api/upload/documento/${did}`);
    proxyUrls.push(`${base}/api/documento-anexo/${did}`);
    proxyUrls.push(`${base}/api/documentos/${did}/anexo`);
  }
  const primary = primaryUrl && String(primaryUrl).trim();
  const fromHist = extractUrlsFromHistoricoAnexos(historicoAnexos);
  const fromContrato = extractUrlsFromAnexosContrato(anexosContrato);
  const all = [primary, ...fromHist, ...fromContrato].filter(Boolean);
  const apiFirst = all.filter((u) => UUID_IN_UPLOAD_PATH.test(u));
  const rest = all.filter((u) => !UUID_IN_UPLOAD_PATH.test(u));
  const ordered = [];
  const seen = new Set();
  const push = (u) => {
    if (!u || seen.has(u)) return;
    seen.add(u);
    ordered.push(u);
  };
  for (const u of proxyUrls) push(u);
  for (const u of apiFirst) push(u);
  for (const u of rest) push(u);
  return ordered;
}

/**
 * Matching com documentos: mesmo conjunto de candidatas (histórico + rotas por id).
 * @param {Record<string, unknown>} doc
 * @param {string} [documentoId] — se omitido, usa `doc.id`
 */
export function collectAnexoDownloadCandidatesForDocumento(doc, documentoId) {
  const id = String(documentoId ?? doc?.id ?? '').trim();
  const primary = String(doc?.anexo_url ?? doc?.anexoUrl ?? doc?.url ?? '').trim();
  return collectAnexoDownloadCandidates(primary || undefined, {
    historicoAnexos: doc?.historico_anexos ?? doc?.historicoAnexos,
    documentoId: id || undefined,
  });
}

/**
 * Matching com atestados: equivalente ao fluxo de documentos — junta `anexos_contrato` ao “histórico”
 * para tentar `/api/upload/:uuid` antes do link legado em `/uploads/`.
 * @param {Record<string, unknown>} atestado
 * @param {string} [atestadoTecnicoId] — se omitido, usa `atestado.id`
 */
export function collectAnexoDownloadCandidatesForAtestado(atestado, atestadoTecnicoId) {
  const id = String(atestadoTecnicoId ?? atestado?.id ?? '').trim();
  const primary = String(atestado?.anexo_url ?? atestado?.anexoUrl ?? '').trim();
  const histArr = atestado?.historico_anexos ?? atestado?.historicoAnexos;
  const fromHist = Array.isArray(histArr) ? [...histArr] : [];
  const fromContrato = extractUrlsFromAnexosContrato(
    atestado?.anexos_contrato ?? atestado?.anexosContrato
  );
  const historicoMerged = [...fromHist, ...fromContrato];
  return collectAnexoDownloadCandidates(primary || undefined, {
    historicoAnexos: historicoMerged,
    atestadoTecnicoId: id || undefined,
  });
}

/**
 * Tenta cada URL até obter blob; em 404 tenta a próxima (útil quando `anexo_url` ainda aponta para `/uploads/` antigo mas o histórico tem `/api/upload/:uuid`).
 */
export async function fetchAnexoAsBlobWithFallbacks(candidateUrls) {
  const list = Array.isArray(candidateUrls) ? candidateUrls.filter(Boolean) : [];
  if (!list.length) throw new Error('URL ausente');
  const unique = [];
  const seen = new Set();
  for (const u of list) {
    if (seen.has(u)) continue;
    seen.add(u);
    unique.push(u);
  }
  const dbg = isAnexoDownloadDebugEnabled();
  if (dbg) {
    console.debug('[Auction anexo]', unique.length, 'URL(s) candidata(s):', unique);
  }
  let lastErr;
  for (const url of unique) {
    try {
      const blob = await fetchAnexoAsBlob(url);
      if (dbg) console.debug('[Auction anexo] OK:', url);
      return blob;
    } catch (e) {
      lastErr = e;
      if (dbg) {
        console.debug('[Auction anexo] falhou:', url, e?.status ?? '', e?.message ?? e);
      }
      const status = e?.status;
      const msg = String(e?.message || '');
      const is404 = status === 404 || msg.includes('404');
      if (!is404) throw e;
    }
  }
  const onlyLegacyDiskUrls = unique.every(
    (u) => /\/uploads\//i.test(String(u)) && !/\/anexo$/i.test(String(u))
  );
  if (onlyLegacyDiskUrls && lastErr) {
    throw new Error(
      'O PDF não foi encontrado no servidor (link antigo em /uploads/). Reenvie o arquivo no cadastro do documento ou atestado para atualizar o anexo.'
    );
  }
  if (lastErr) throw lastErr;
  throw new Error('Falha ao baixar anexo');
}

/** Baixa binário de anexo (UploadBlob autenticado ou ficheiro estático em /uploads/ na API). */
export async function fetchAnexoAsBlob(url) {
  const resolved = resolveAttachmentFetchUrl(url);
  if (!resolved) throw new Error('URL ausente');
  if (isEntityAnexoDownloadUrl(resolved)) {
    return fetchAnexoBinaryWithBearer(resolved);
  }
  if (requiresUploadBlobAuth(resolved) || parseUploadBlobIdFromFileUrl(resolved)) {
    return fetchUploadBlob(resolved);
  }
  return fetchAnexoBinaryWithBearer(resolved);
}

/** Abre o arquivo de upload em nova aba (com token). Para URLs que não são da nossa API, abre direto. */
export async function openUploadInNewTab(fileUrl) {
  if (!requiresUploadBlobAuth(fileUrl)) {
    window.open(fileUrl, '_blank', 'noopener,noreferrer');
    return;
  }
  const blob = await fetchUploadBlob(fileUrl);
  const blobUrl = URL.createObjectURL(blob);
  window.open(blobUrl, '_blank', 'noopener,noreferrer');
  setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
}

/** Baixa o arquivo de upload com nome sugerido (com token). Para URLs que não são da nossa API, usa link direto. */
export async function downloadUploadFile(fileUrl, fileName) {
  if (!requiresUploadBlobAuth(fileUrl)) {
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = fileName || 'download';
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    return;
  }
  const blob = await fetchUploadBlob(fileUrl);
  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = fileName || 'download';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(blobUrl);
}

/** Upload de imagem do Social (Feed): persiste no banco e retorna URL estável. */
export async function uploadSocialImage(file) {
  const formData = new FormData();
  formData.append('file', file);
  const token = getToken();
  const base = getBaseUrl() || '';
  const res = await fetch(`${base}/api/social/upload-imagem`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    credentials: 'include',
    mode: 'cors',
    body: formData,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw Object.assign(new Error(data.error || res.statusText), { status: res.status, data });
  return data;
}

// Integrações (stubs ou implementação no backend)
export async function invokeLLM(params) {
  return api.post('/api/integrations/invoke-llm', params);
}

export async function extractDataFromUploadedFile(params) {
  return api.post('/api/integrations/extract-data', params);
}

/** Extrai o texto integral de PDFs/imagens (sem IA), para cache de sessão da "Pergunte a IA". */
export async function extractTextFromFiles(params) {
  return api.post('/api/integrations/extract-text', params);
}

/**
 * Invoca a IA em modo streaming (SSE). Chama `onDelta(textoParcial, textoAcumulado)` a cada
 * pedaço gerado e resolve com o texto completo. Use `signal` para cancelar.
 * @param {{ systemPrompt?: string, prompt: string, maxTokens?: number, signal?: AbortSignal, onDelta?: (delta: string, full: string) => void }} params
 * @returns {Promise<string>}
 */
export async function streamInvokeLLM({ systemPrompt, prompt, maxTokens, signal, onDelta } = {}) {
  const url = `${getBaseUrl()}/api/integrations/invoke-llm-stream`;
  const token = getToken();
  const timeoutMs = 600_000;
  const controller = new AbortController();
  const onParentAbort = () => controller.abort();
  if (signal) {
    if (signal.aborted) controller.abort();
    else signal.addEventListener('abort', onParentAbort, { once: true });
  }
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  let res;
  try {
    res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      credentials: 'include',
      mode: 'cors',
      body: JSON.stringify({
        systemPrompt,
        prompt,
        ...(maxTokens != null ? { maxTokens } : {}),
      }),
      signal: controller.signal,
    });
  } catch (err) {
    if (err?.name === 'AbortError') {
      throw Object.assign(new Error('A requisição demorou muito para responder. Tente novamente.'), {
        status: 408,
        name: 'AbortError',
      });
    }
    throw err;
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener?.('abort', onParentAbort);
  }

  if (!res.ok || !res.body) {
    let data = {};
    try {
      data = await res.json();
    } catch {
      // sem corpo JSON
    }
    throw Object.assign(new Error(data.message || data.error || `Erro ${res.status}`), {
      status: res.status,
      data,
    });
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let full = '';

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let idx;
    while ((idx = buffer.indexOf('\n\n')) >= 0) {
      const frame = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);
      const line = frame.split('\n').find((l) => l.startsWith('data:'));
      if (!line) continue;
      const payload = line.slice(5).trim();
      if (!payload) continue;
      let obj;
      try {
        obj = JSON.parse(payload);
      } catch {
        continue;
      }
      if (obj.error) {
        throw Object.assign(new Error(obj.error), { status: 502 });
      }
      if (typeof obj.delta === 'string') {
        full += obj.delta;
        if (onDelta) onDelta(obj.delta, full);
      }
      if (obj.done) return full;
    }
  }
  return full;
}

// Auth
export const authApi = {
  login: (email, senha) => api.post('/auth/login', { email, senha }),
  loginSuperAdmin: (email, senha) => api.post('/auth/super-admin/login', { email, senha }),
  esqueciSenha: (email) => api.post('/auth/esqueci-senha', { email }),
  redefinirSenha: (token, novaSenha, confirmaSenha) =>
    api.post('/auth/redefinir-senha', { token, novaSenha, confirmaSenha }),
  alterarSenha: (senhaAtual, novaSenha, confirmaSenha) =>
    api.post('/auth/alterar-senha', { senhaAtual, novaSenha, confirmaSenha }),
  me: () => api.get('/auth/me'),
  guidedTour: () => api.get('/auth/guided-tour'),
  ackGuidedTourIntro: () => api.post('/auth/guided-tour/ack-intro', {}),
  empresasVinculadas: () => api.get('/auth/empresas-vinculadas'),
  trocarEmpresa: (empresaId) => api.post('/auth/trocar-empresa', { empresaId }),
  updateMe: (data) => api.patch('/auth/me', data),
};

// Admin (Super Admin) - usa token próprio
async function adminRequest(method, path, body = undefined) {
  const url = path.startsWith('http') ? path : `${getBaseUrl()}${path}`;
  const token = getSuperAdminToken();
  if (!token) {
    const err = new Error('Super admin não autenticado');
    err.status = 401;
    throw err;
  }
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
  const config = {
    method,
    headers,
    credentials: 'include',
    mode: 'cors',
    ...(body != null && { body: JSON.stringify(body) }),
  };
  const res = await fetch(url, config);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.error || data.message || res.statusText);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

export const apiAdmin = {
  get: (path, params) => {
    const q = params && Object.keys(params).length ? '?' + new URLSearchParams(params).toString() : '';
    return adminRequest('GET', path + q);
  },
  post: (path, body) => adminRequest('POST', path, body),
  put: (path, body) => adminRequest('PUT', path, body),
  delete: (path) => adminRequest('DELETE', path),
  /** Tour guiado (Super Admin): GET devolve { effective, storedRaw }; PUT body JSON; POST reset sem body. */
  guidedTourGet: () => adminRequest('GET', '/api/admin/config/guided-tour'),
  guidedTourPut: (body) => adminRequest('PUT', '/api/admin/config/guided-tour', body),
  guidedTourReset: () => adminRequest('POST', '/api/admin/config/guided-tour/reset', {}),
};

export const adminSuporteApi = {
  listarTickets: (params = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v != null && String(v).trim() !== '') q.set(k, String(v));
    });
    return adminRequest('GET', `/api/admin/suporte/tickets${q.toString() ? `?${q.toString()}` : ''}`);
  },
  obterTicket: (id) => adminRequest('GET', `/api/admin/suporte/tickets/${id}`),
  atualizarStatus: (id, payload) => adminRequest('PATCH', `/api/admin/suporte/tickets/${id}/status`, payload),
  resumoPorEmpresa: () => adminRequest('GET', '/api/admin/suporte/resumo-por-empresa'),
  uploadResolucaoAnexos: async (id, files = []) => {
    const token = getSuperAdminToken();
    if (!token) {
      const err = new Error('Super admin não autenticado');
      err.status = 401;
      throw err;
    }
    const fd = new FormData();
    files.forEach((f) => fd.append('files', f));
    const res = await fetch(`${getBaseUrl()}/api/admin/suporte/tickets/${id}/resolucao-anexos`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      credentials: 'include',
      mode: 'cors',
      body: fd,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const err = new Error(data.error || data.message || res.statusText);
      err.status = res.status;
      err.data = data;
      throw err;
    }
    return data;
  },
  openAnexo: async (uploadBlobId) => {
    const token = getSuperAdminToken();
    if (!token) {
      const err = new Error('Super admin não autenticado');
      err.status = 401;
      throw err;
    }
    const res = await fetch(`${getBaseUrl()}/api/admin/suporte/anexos/${uploadBlobId}`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
      credentials: 'include',
      mode: 'cors',
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      const err = new Error(data.error || data.message || res.statusText);
      err.status = res.status;
      err.data = data;
      throw err;
    }
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    window.open(blobUrl, '_blank', 'noopener,noreferrer');
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
  },
};

/** Nome do ficheiro a partir de Content-Disposition (RFC 5987 e forma simples). */
function parseContentDispositionFilename(cd) {
  if (!cd || typeof cd !== 'string') return null;
  const star = cd.match(/filename\*=UTF-8''([^;\s]+)/i);
  if (star) {
    try {
      return decodeURIComponent(star[1].trim());
    } catch {
      return star[1].trim();
    }
  }
  const quoted = cd.match(/filename="([^"]+)"/i);
  if (quoted) return quoted[1];
  const plain = cd.match(/filename=([^;\s]+)/i);
  if (plain) return plain[1].replace(/^["']|["']$/g, '');
  return null;
}

const MANIFESTO_PDF_FETCH_TIMEOUT_MS = 120_000;

/**
 * GET/POST PDF do Manifesto com Bearer; retorna blob + nome (Content-Disposition).
 * @param {string} path
 * @param {{ method?: string, body?: object }} [opts]
 */
async function fetchManifestoPdf(path, opts = {}) {
  const { method = 'GET', body } = opts;
  const url = path.startsWith('http') ? path : `${getBaseUrl()}${path}`;
  const token = getToken();
  const controller = new AbortController();
  const tid = setTimeout(() => controller.abort(), MANIFESTO_PDF_FETCH_TIMEOUT_MS);
  try {
    const res = await fetch(url, {
      method,
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
        Accept: 'application/pdf, application/json',
        ...(body != null ? { 'Content-Type': 'application/json' } : {}),
      },
      credentials: 'include',
      mode: 'cors',
      body: body != null ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    });
    const parsed = parseContentDispositionFilename(res.headers.get('Content-Disposition'));
    if (!res.ok) {
      const ct = res.headers.get('Content-Type') || '';
      const data = ct.includes('json') ? await res.json().catch(() => ({})) : {};
      if (res.status === 401) {
        setToken(null);
      }
      const err = new Error(data.error || data.message || res.statusText);
      err.status = res.status;
      err.data = data;
      throw err;
    }
    const blob = await res.blob();
    return { blob, filename: parsed || 'manifesto.pdf' };
  } finally {
    clearTimeout(tid);
  }
}

const EXPORTACAO_COMPLETA_TIMEOUT_MS = 900_000;

// Empresas (registro público + rotas com token)
export const empresasApi = {
  registro: (data) => api.post('/empresas/registro', data),
  verificarCnpj: (cnpj) => api.get(`/empresas/verificar-cnpj?cnpj=${encodeURIComponent(cnpj)}`),
  minhaEmpresa: () => api.get('/empresas/minha-empresa'),
  atualizarEmpresa: (data) => api.put('/empresas/minha-empresa', data),
  planos: () => api.get('/empresas/planos'),
  assinarPlano: (planoId, tipo) => api.post('/empresas/assinar-plano', { plano_id: planoId, tipo }),
  faturas: () => api.get('/empresas/faturas'),
  uso: () => api.get('/empresas/uso'),
  /**
   * GET binário: ZIP com exportação completa (portabilidade). Timeout alargado para muitos anexos.
   * @param {{ onProgress?: (p: { loaded: number; total: number | null; percent: number | null }) => void }} [opts]
   */
  downloadExportacaoCompleta: async (opts = {}) => {
    const { onProgress } = opts;
    const path = '/empresas/exportacao-completa';
    const url = path.startsWith('http') ? path : `${getBaseUrl()}${path}`;
    const token = getToken();
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), EXPORTACAO_COMPLETA_TIMEOUT_MS);
    try {
      const res = await fetch(url, {
        method: 'GET',
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
          Accept: 'application/zip, application/json',
        },
        credentials: 'include',
        mode: 'cors',
        signal: controller.signal,
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        if (res.status === 401) {
          setToken(null);
        }
        const err = new Error(data.error || data.message || res.statusText);
        err.status = res.status;
        err.data = data;
        throw err;
      }
      const parsed = parseContentDispositionFilename(res.headers.get('Content-Disposition'));
      const filename = parsed || 'auction-export.zip';
      const contentType = res.headers.get('Content-Type') || 'application/zip';
      const lenHeader = res.headers.get('Content-Length');
      const total =
        lenHeader != null && lenHeader !== '' ? parseInt(lenHeader, 10) : null;
      const totalOk = total != null && Number.isFinite(total) && total > 0 ? total : null;

      if (!res.body || typeof res.body.getReader !== 'function') {
        const blob = await res.blob();
        if (typeof onProgress === 'function') {
          onProgress({ loaded: blob.size, total: totalOk, percent: totalOk ? 100 : null });
        }
        return { blob, filename };
      }

      const reader = res.body.getReader();
      const chunks = [];
      let loaded = 0;
      if (typeof onProgress === 'function') {
        onProgress({ loaded: 0, total: totalOk, percent: totalOk ? 0 : null });
      }
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        if (value && value.length) {
          chunks.push(value);
          loaded += value.length;
          if (typeof onProgress === 'function') {
            const percent =
              totalOk != null ? Math.min(100, Math.round((loaded / totalOk) * 100)) : null;
            onProgress({ loaded, total: totalOk, percent });
          }
        }
      }
      const blob = new Blob(chunks, { type: contentType });
      if (typeof onProgress === 'function') {
        onProgress({
          loaded: blob.size,
          total: totalOk ?? blob.size,
          percent: 100,
        });
      }
      return { blob, filename };
    } finally {
      clearTimeout(timeoutId);
    }
  },
  usuarios: {
    list: () => api.get('/empresas/usuarios'),
    create: (data) => api.post('/empresas/usuarios', data),
    update: (id, data) => api.put(`/empresas/usuarios/${id}`, data),
    delete: (id) => api.delete(`/empresas/usuarios/${id}`),
  },
  convites: {
    enviarEmail: (data) => api.post('/empresas/convites/enviar-email', data),
    sync: () => api.post('/empresas/convites/sync'),
  },
};

// ── Pulse — Automação Assistida de Lances ────────────────────────────────────

export const pulseApi = {
  // Perfis de estratégia
  listProfiles: () => api.get('/api/pulse/profiles'),
  createProfile: (data) => api.post('/api/pulse/profiles', data),
  updateProfile: (id, data) => api.patch(`/api/pulse/profiles/${id}`, data),

  // Sessões
  listSessions: (params) => api.get('/api/pulse/sessions', { params }),
  startSession: (data) => api.post('/api/pulse/sessions/start', data),
  pauseSession: (id) => api.post(`/api/pulse/sessions/${id}/pause`),
  resumeSession: (id) => api.post(`/api/pulse/sessions/${id}/resume`),
  stopSession: (id) => api.post(`/api/pulse/sessions/${id}/stop`),
  getStatus: (id) => api.get(`/api/pulse/sessions/${id}/status`),
  getEvents: (id, params) => api.get(`/api/pulse/sessions/${id}/events`, { params }),
  confirmBid: (id, payload) => api.post(`/api/pulse/sessions/${id}/confirm-bid`, payload),
  ackIntervention: (id, notes) => api.post(`/api/pulse/sessions/${id}/ack-intervention`, { notes }),
  getAudit: (id) => api.get(`/api/pulse/sessions/${id}/audit`),

  // PNCP lookup
  lookupPncp: (params) => api.get('/api/pulse/pncp/lookup', { params }),

  // Alvos (grupos/itens) por sessão
  listAlvos: (sessionId) => api.get(`/api/pulse/sessions/${sessionId}/alvos`),
  upsertAlvos: (sessionId, data) => api.post(`/api/pulse/sessions/${sessionId}/alvos`, data),
  updateAlvo: (sessionId, alvoId, data) => api.patch(`/api/pulse/sessions/${sessionId}/alvos/${alvoId}`, data),
};
